import { useState, useMemo, useEffect } from 'react';
import {
  Zap, Droplets, Thermometer, Gauge, Wind, CheckCircle2, AlertCircle,
  Info, RotateCcw, FlaskConical, Settings, Beaker, Activity,
  ShieldCheck, Flame, Layers,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine,
} from 'recharts';

/* =========================================================================
   ELECTROCHLORINATION PLANT - SPECIFICATION & SIZING TOOL
   On-site sodium hypochlorite generation from seawater via electrolysis.
   Typical applications: seawater intake biofouling control (power stations,
   desalination plants, offshore platforms), ballast-water disinfection,
   cooling-tower make-up disinfection.

   Engineering basis (public-domain):
     - Faraday's law of electrolysis for Cl2 yield
     - Bommaraju & Orosco "Brine Electrolysis", ECS
     - Bennett (1980) "Electrodes for the generation of Cl2 / O2 from seawater"
     - De Nora / Evoqua / Severn Trent commercial envelopes
     - IEC 60079 / DSEAR for H2 vent safety (LEL 4 % v/v in air)
   ========================================================================= */

// ---------------------------------------------------------------------------
// Standard seawater property helpers
// ---------------------------------------------------------------------------

/** Seawater density (kg/m^3) - simplified El-Dessouky & Ettouny correlation. */
function swDensity(salinity_ppt: number, T_C: number): number {
  const dT = T_C - 20;
  return 1000 + 0.808 * salinity_ppt - 0.0708 * dT - 0.003 * dT * dT + 0.0005 * salinity_ppt * dT;
}

/** Seawater electrical conductivity (mS/cm) - UNESCO PSS-78 simplified. */
function swConductivity(salinity_ppt: number, T_C: number): number {
  const Cref = 42.914 * (salinity_ppt / 35);
  const tempFactor = 1 + 0.0205 * (T_C - 15) - 5.5e-5 * (T_C - 15) * (T_C - 15);
  return Cref * tempFactor;
}

/** Theoretical Cl2 yield per Faraday at 100 % efficiency (g/Ah). */
const CL2_PER_AH = (71 / (2 * 96485)) * 3600; // = 1.3245 g/Ah

// ---------------------------------------------------------------------------
// Param types
// ---------------------------------------------------------------------------

type CellTech = 'monopolar' | 'bipolar';
type AcidType = 'HCl_15pc' | 'HCl_30pc' | 'sulphamic_5pc';
type DutyMode = 'continuous' | 'shock' | 'cyclic';

/**
 * Standard pipe sizes for electrochlorination cell brine connections.
 * Inside diameters are Sch 80 PVDF (BS EN ISO 15493 / ASTM F441) typical for
 * NaOCl / seawater service to BS EN 1092-1 / ASME B16.5 flange faces.
 * Manufacturers (De Nora, Severn Trent, Evoqua, Hidrostal) supply cells with
 * these nominal connection sizes - choose the smallest DN that satisfies the
 * brine-velocity envelope (0.4 - 1.5 m/s typical for cell nozzles).
 */
const STD_PIPES: { dn: number; id_mm: number; max_flow_m3h: number; label: string }[] = [
  { dn: 25,  id_mm: 26.6,  max_flow_m3h:  2.5, label: 'DN25 / 1"' },
  { dn: 32,  id_mm: 35.0,  max_flow_m3h:  4.5, label: 'DN32 / 1-1/4"' },
  { dn: 40,  id_mm: 41.0,  max_flow_m3h:  6.5, label: 'DN40 / 1-1/2"' },
  { dn: 50,  id_mm: 52.5,  max_flow_m3h: 10.5, label: 'DN50 / 2"' },
  { dn: 65,  id_mm: 68.8,  max_flow_m3h: 18.0, label: 'DN65 / 2-1/2"' },
  { dn: 80,  id_mm: 81.7,  max_flow_m3h: 25.5, label: 'DN80 / 3"' },
  { dn: 100, id_mm: 107.1, max_flow_m3h: 44.0, label: 'DN100 / 4"' },
  { dn: 125, id_mm: 131.7, max_flow_m3h: 66.0, label: 'DN125 / 5"' },
  { dn: 150, id_mm: 159.3, max_flow_m3h: 95.0, label: 'DN150 / 6"' },
  { dn: 200, id_mm: 213.7, max_flow_m3h: 170,  label: 'DN200 / 8"' },
  { dn: 250, id_mm: 263.0, max_flow_m3h: 260,  label: 'DN250 / 10"' },
  { dn: 300, id_mm: 312.7, max_flow_m3h: 365,  label: 'DN300 / 12"' },
];

/** Returns the smallest DN whose max flow envelope >= demand. */
function recommendDN(flow_m3h: number, vel_target_ms = 1.0): number {
  // velocity-driven: A = Q / v; D = sqrt(4 A / pi)
  const Q_m3s = flow_m3h / 3600;
  const D_req_mm = Math.sqrt(4 * Q_m3s / Math.max(0.1, vel_target_ms) / Math.PI) * 1000;
  // Pick the smallest standard ID >= required
  const hit = STD_PIPES.find(s => s.id_mm >= D_req_mm) ?? STD_PIPES[STD_PIPES.length - 1];
  return hit.dn;
}

/** Lookup the standard pipe by DN (or fallback to first). */
function lookupDN(dn: number) {
  return STD_PIPES.find(s => s.dn === dn) ?? STD_PIPES[0];
}

/** Snap to nearest standard DN >= the recommended size, with sane lower bound. */
function snapDN(rec: number): number {
  return STD_PIPES.find(s => s.dn >= rec)?.dn ?? STD_PIPES[STD_PIPES.length - 1].dn;
}

/* ============================================================
   Recommendation engines - all sized from seawater flow rate.
   Each returns a partial-state patch to merge with setP().
   ============================================================ */

/** Sizing recipe for the electrolyser stack. */
function recommendStack(p: ECParams): Partial<ECParams> {
  // Cl2 demand kg/h
  const cl2_kgh = p.hypochloriteOutput_kgh;
  // Pick module count so each delivers <= 5 kg Cl2/h (industry mid-size cell)
  const modules = Math.max(1, Math.ceil(cl2_kgh / 5));
  // Total electrolyser inlet ~ 1 m3/h brine per 1 kg Cl2/h product
  const elecInlet = Math.max(2, Math.min(200, cl2_kgh * 1.0));
  const brineFlow = elecInlet / modules;
  return {
    electrolyserInlet_m3h: parseFloat(elecInlet.toFixed(2)),
    modules_installed: modules,
    modules_standby:   1,
    cells_per_module:  20,
    currentDensity_kAm2: 3.0,
    cellVoltage_V:     4.0,
    currentEfficiency_pc: 90,
    brineVelocity_ms:  0.6,
    cell_ratedDC_current_A:   Math.ceil(((cl2_kgh / modules * 1000) / (CL2_PER_AH * 0.9)) * 1.15 / 50) * 50,
    cell_ratedDC_voltage_V:   61,
    cell_ratedCapacity_kgCl2h: parseFloat((cl2_kgh / modules * 1.15).toFixed(2)),
    cell_operatingPressure_barg: 1.5,
    cell_inlet_DN:  snapDN(recommendDN(brineFlow, 1.2)),
    cell_outlet_DN: snapDN(recommendDN(brineFlow, 1.0)),
  };
}

/** Sizing recipe for the NaOCl dosing skid. */
function recommendDosing(p: ECParams): Partial<ECParams> {
  const cl2_kgh = p.hypochloriteOutput_kgh;
  /* derives the NaOCl product flow from the Cl2 mass output */
  // NaOCl product ~ 0.6 % w/w typical
  const naocl_strength = 0.006;
  const naocl_density  = 1009; // kg/m3
  const flow_total_Lh = (cl2_kgh * 1000) / (naocl_strength * naocl_density / 1000);
  // Choose duty pump count by flow band
  const duty = flow_total_Lh < 200 ? 1 : flow_total_Lh < 1000 ? 2 : 3;
  const per_pump = flow_total_Lh / duty;
  return {
    dosingPumps_duty:    duty,
    dosingPumps_standby: 1,
    dosingPump_capacity_Lh_each: parseFloat((per_pump * 1.1).toFixed(0)), // 10 % margin
    dosingPumpHead_bar:  4,
    dosingPump_staticPressure_mbar: 500,
    dosingPump_inlet_DN:  snapDN(recommendDN(per_pump / 1000, 0.5)),
    dosingPump_outlet_DN: snapDN(recommendDN(per_pump / 1000, 1.0)),
    storageTank_h:       2,
  };
}

/** Sizing recipe for the H2 dilution blowers. */
function recommendBlower(p: ECParams): Partial<ECParams> {
  const cl2_kgh = p.hypochloriteOutput_kgh;
  const H2 = cl2_kgh * 0.316;
  const air = H2 * 40; // dilution factor 40
  // 1 duty + 1 standby for systems up to 500 Nm3/h air; else 2+1
  const duty = air < 500 ? 1 : 2;
  const per_blower = air * 1.25 / duty;
  return {
    vent_dilution_factor: 40,
    blower_safety_factor: 1.25,
    blower_duty:         duty,
    blower_standby:      1,
    blower_static_mbar:  60,
    blower_purge_min:    15,
    blower_inlet_DN:  snapDN(recommendDN(per_blower, 12)),
    blower_outlet_DN: snapDN(recommendDN(per_blower, 10)),
  };
}

/** Sizing recipe for the acid wash CIP system. */
function recommendAcid(p: ECParams): Partial<ECParams> {
  // Hardness-based interval
  const totalHardness = p.calciumHardness_mgL + p.magnesiumHardness_mgL;
  const interval = Math.max(7, Math.min(120, 30 * (6500 / Math.max(100, totalHardness))));
  // Tank ~ 0.5 m3 per module (allow for CIP loop + spent acid drain)
  const modules = Math.max(1, p.modules_installed);
  const tank_m3 = Math.max(0.5, Math.min(15, modules * 0.5));
  const duration_min = 90;
  const flow_total_Lh = (tank_m3 * 1000) / (duration_min / 60);
  return {
    acidType:               'HCl_15pc' as AcidType,
    acidWashEvery_d:        Math.round(interval),
    acidWashDuration_min:   duration_min,
    acidWashConc_target_pc: 5,
    acidWashTank_m3:        parseFloat(tank_m3.toFixed(1)),
    acidPumpHead_bar:       3,
    acidPump_staticPressure_mbar: 400,
    acidPumps_duty:         1,
    acidPumps_standby:      1,
    acidPump_inlet_DN:  snapDN(recommendDN(flow_total_Lh / 1000, 0.5)),
    acidPump_outlet_DN: snapDN(recommendDN(flow_total_Lh / 1000, 1.0)),
  };
}

/** All-recommendations bundle. */
function recommendAll(p: ECParams): Partial<ECParams> {
  const a = recommendStack(p);
  const merged = { ...p, ...a };
  const b = recommendDosing(merged);
  const merged2 = { ...merged, ...b };
  const c = recommendBlower(merged2);
  const merged3 = { ...merged2, ...c };
  const d = recommendAcid(merged3);
  return { ...a, ...b, ...c, ...d };
}

interface ECParams {
  // ---- Plant flow architecture ----
  seawaterFlow_PDoff_m3h: number;   // max treated-water flow (PD = pressure-drop OFF)
  electrolyserInlet_m3h:  number;   // brine flow into the electrolyser stack
  hypochloriteOutput_kgh: number;   // product Cl2 mass output from the electrolyser
  // ---- Seawater chemistry (standard) ----
  salinity_ppt:     number;
  temperature_C:    number;
  pH_feed:          number;
  TSS_mgL:          number;
  turbidity_NTU:    number;
  // ---- Detailed seawater chemistry (extended) ----
  TDS_mgL:            number;  // total dissolved solids
  chloride_mgL:       number;  // Cl- - 19 000 at S=35
  bromide_mgL:        number;  // Br- - ~ 65 at S=35
  sulphate_mgL:       number;  // SO4^2-
  calciumHardness_mgL: number; // mg/L as CaCO3
  magnesiumHardness_mgL: number; // mg/L as CaCO3
  totalAlkalinity_mgL: number;  // mg/L as CaCO3
  freeCO2_mgL:        number;
  silica_mgL:         number;
  ironMn_mgL:         number;  // total Fe + Mn (electrode-fouling risk)
  organic_TOC_mgL:    number;  // total organic carbon
  // ---- Cell technology ----
  cellTech:          CellTech;
  currentDensity_kAm2: number;
  cellVoltage_V:     number;
  currentEfficiency_pc: number;
  cells_per_module:  number;
  modules_installed: number;
  modules_standby:   number;
  // ---- Brine side ----
  brineVelocity_ms:         number;
  // ---- Cell rated operating conditions (nameplate) ----
  cell_ratedDC_current_A:   number;   // rated DC current per module
  cell_ratedDC_voltage_V:   number;   // rated DC voltage per module
  cell_ratedCapacity_kgCl2h: number;  // nameplate Cl2 production per module
  cell_operatingPressure_barg: number; // brine-side gauge pressure
  cell_inlet_DN:            number;   // standard DN of inlet flange
  cell_outlet_DN:           number;   // standard DN of outlet flange
  // ---- Hypochlorite dosing ----
  dosingPumps_duty:    number;
  dosingPumps_standby: number;
  dosingPumpHead_bar:  number;
  dosingPump_staticPressure_mbar: number;
  dosingPump_capacity_Lh_each: number;   // user-set capacity per duty pump (L/h) - 0 = derived
  dosingPump_inlet_DN: number;            // standard pipe (suction)
  dosingPump_outlet_DN: number;           // standard pipe (discharge)
  storageTank_h:       number;
  // ---- Hydrogen dilution / vent (blowers) ----
  vent_dilution_factor: number;
  blower_safety_factor: number;     // 1.1 - 1.5 sizing margin over computed
  blower_purge_min:    number;      // pre-energise + post-shutdown purge minutes
  blower_static_mbar:  number;      // total static pressure at blower discharge (mbar)
  blower_duty:         number;
  blower_standby:      number;
  blower_inlet_DN:     number;            // standard pipe (suction / air intake)
  blower_outlet_DN:    number;            // standard pipe (vent discharge)
  // ---- H2 tank-headspace target (drives blower auto-sizing) ----
  h2_target_pct_vv:    number;      // max equilibrium % H2 v/v in tank headspace (1% = 25% LEL)
  h2_tank_volume_m3:   number;      // tank/enclosure headspace volume to ventilate
  // ---- Acid wash ----
  acidType:           AcidType;
  acidWashEvery_d:    number;
  acidWashDuration_min: number;
  acidWashConc_target_pc: number;
  acidWashTank_m3:    number;
  acidPumpHead_bar:   number;
  acidPump_staticPressure_mbar: number;
  acidPumps_duty:     number;
  acidPumps_standby:  number;
  acidPump_inlet_DN:  number;
  acidPump_outlet_DN: number;
  // ---- Working conditions (apply to ALL devices) ----
  workingTemp_C:      number;       // ambient / electrical-room temperature for blowers, pumps, panels
  workingPressure_mbar_atm: number; // local atmospheric pressure (sea-level default ~1013)
  // ---- Seawater pre-filter (sized from PD-off inflow) ----
  filter_micron_um:        number;  // 80 - 100 typical for electrochlorination
  filter_designDP_bar:     number;  // clean-to-dirty DP trigger
  filter_rate_m3_m2_h:     number;  // design filtration rate
  filter_inlet_DN:         number;  // standard pipe
  filter_outlet_DN:        number;  // standard pipe
  // ---- Transformer / rectifier (datasheet) ----
  txr_AC_primary_V:  number;        // AC supply (typ 400 / 690 V)
  txr_efficiency_pc: number;        // overall transformer + rectifier efficiency
  txr_redundancy:    'N' | 'N+1';   // installed redundancy
  // ---- NaOCl storage + H2 degassing tank ----
  storage_tank_volume_m3: number;
  storage_degassing_residence_min: number; // residence time for H2 disengagement
  storage_material:  'GRP' | 'HDPE' | 'PP' | 'PVDF';
  storage_freeboard_pc: number;     // % above design liquid level for vent gas-space
  // ---- Eductor (NaOCl dosing motive) ----
  eductor_motive_m3h:    number;    // seawater motive flow
  eductor_suction_Lh:    number;    // NaOCl suction flow
  eductor_motive_DN:     number;
  eductor_suction_DN:    number;
  eductor_discharge_DN:  number;
  eductor_motive_pressure_bar: number;
  // ---- Duty cycle ----
  dutyMode:          DutyMode;
  shockHoursPerDay:  number;
  // Cyclic mode — repeating ON/OFF pattern (e.g. 30 min ON / 30 min OFF, or 20/20)
  cycleOnMin:        number;
  cycleOffMin:       number;
}

const DEFAULTS: ECParams = {
  // headline plant flows
  seawaterFlow_PDoff_m3h: 22.5,
  electrolyserInlet_m3h:  15.0,
  hypochloriteOutput_kgh: 15.0,
  salinity_ppt:     35,
  temperature_C:    20,
  pH_feed:          8.1,
  TSS_mgL:          20,
  turbidity_NTU:    3,
  // detailed chemistry (default = standard open-ocean seawater at S=35 ppt)
  TDS_mgL:               35000,
  chloride_mgL:          19000,
  bromide_mgL:           65,
  sulphate_mgL:          2700,
  calciumHardness_mgL:   1050,
  magnesiumHardness_mgL: 5500,
  totalAlkalinity_mgL:   120,
  freeCO2_mgL:           1.5,
  silica_mgL:            2.0,
  ironMn_mgL:            0.05,
  organic_TOC_mgL:       1.0,
  cellTech:           'bipolar',
  currentDensity_kAm2: 3.0,
  cellVoltage_V:       4.0,
  currentEfficiency_pc: 90,
  cells_per_module:    20,
  modules_installed:   1,
  modules_standby:     1,
  brineVelocity_ms:         0.6,
  cell_ratedDC_current_A:    1100,  // nameplate current - calculated (Faraday) by default
  cell_ratedDC_voltage_V:    61,    // nameplate DC voltage per module (adjustable)
  cell_ratedCapacity_kgCl2h: 1.3,   // nameplate Cl2 yield = 1.3245 g/Ah x 0.9 x 1100 A
  cell_operatingPressure_barg: 1.5,
  cell_inlet_DN:             80,
  cell_outlet_DN:            80,
  dosingPumps_duty:    1,
  dosingPumps_standby: 1,
  dosingPumpHead_bar:  4,
  dosingPump_staticPressure_mbar: 500,
  dosingPump_capacity_Lh_each: 0,   // 0 = derived from demand
  dosingPump_inlet_DN: 25,
  dosingPump_outlet_DN: 25,
  storageTank_h:       2,
  vent_dilution_factor: 40,
  blower_safety_factor: 1.25,
  blower_purge_min:     15,
  blower_static_mbar:   60,
  blower_duty:          1,
  blower_standby:       1,
  blower_inlet_DN:      80,
  blower_outlet_DN:     100,
  h2_target_pct_vv:     1.0,   // 25 % LEL (industry standard for enclosed brine/H2 spaces)
  h2_tank_volume_m3:    5.0,
  acidType:               'HCl_15pc',
  acidWashEvery_d:        30,
  acidWashDuration_min:   90,
  acidWashConc_target_pc: 5,
  acidWashTank_m3:        2.0,
  acidPumpHead_bar:       3,
  acidPump_staticPressure_mbar: 400,
  acidPumps_duty:         1,
  acidPumps_standby:      1,
  acidPump_inlet_DN:      25,
  acidPump_outlet_DN:     25,
  workingTemp_C:           25,
  workingPressure_mbar_atm: 1013,
  // seawater pre-filter (everything else derived from PD-off seawater flow)
  filter_micron_um:    100,
  filter_designDP_bar: 0.5,
  filter_rate_m3_m2_h: 100,
  filter_inlet_DN:     65,
  filter_outlet_DN:    65,
  // transformer / rectifier (datasheet defaults - 415 V primary, 95 % rectifier x 98 % transformer)
  txr_AC_primary_V:  415,
  txr_efficiency_pc: 93,
  txr_redundancy:    'N+1',
  // NaOCl storage + degassing tank (datasheet GRP package)
  storage_tank_volume_m3: 3.0,
  storage_degassing_residence_min: 20,
  storage_material:  'GRP',
  storage_freeboard_pc: 20,
  // eductor (NaOCl dosing motive)
  eductor_motive_m3h:    7.5,   // motive seawater split from PD-off side
  eductor_suction_Lh:    1500,
  eductor_motive_DN:     50,
  eductor_suction_DN:    25,
  eductor_discharge_DN:  65,
  eductor_motive_pressure_bar: 3.5,
  dutyMode:         'continuous',
  shockHoursPerDay: 4,
  cycleOnMin:       30,
  cycleOffMin:      30,
};

const ACID_PROPS: Record<AcidType, { name: string; concPct: number; sg: number; activeFrac: number; useCase: string }> = {
  HCl_15pc:      { name: 'Hydrochloric acid 15 %', concPct: 15, sg: 1075, activeFrac: 0.15, useCase: 'Standard CIP for electrode descaling (CaCO3 / Mg(OH)2)' },
  HCl_30pc:      { name: 'Hydrochloric acid 30 %', concPct: 30, sg: 1149, activeFrac: 0.30, useCase: 'Heavy fouling / extended interval cleaning' },
  sulphamic_5pc: { name: 'Sulphamic acid 5 %',     concPct:  5, sg: 1020, activeFrac: 0.05, useCase: 'Low-corrosivity option for stainless components' },
};

// ---------------------------------------------------------------------------
// Engineering calculations
// ---------------------------------------------------------------------------
function calcResults(p: ECParams) {
  const rho  = swDensity(p.salinity_ppt, p.temperature_C);
  const cond = swConductivity(p.salinity_ppt, p.temperature_C);

  // ---- Detailed chemistry corrections ----
  // 1. Chloride-availability correction on current efficiency:
  //    Reference: ~ 19 000 mg/L at S=35 ppt -> nominal CE.
  //    Below 8 000 mg/L Cl-, CE drops linearly (less Cl2 vs O2 competition).
  const Cl_ref = 19000;
  const Cl_factor = Math.max(0.55, Math.min(1.02, p.chloride_mgL / Cl_ref));
  const adjusted_CE_pc = Math.max(50, Math.min(98, p.currentEfficiency_pc * Cl_factor));

  // 2. Total hardness drives the recommended wash interval (CaCO3 + Mg(OH)2 scaling).
  const totalHardness_mgL = p.calciumHardness_mgL + p.magnesiumHardness_mgL;
  // Empirical: at 6 500 mg/L (open ocean) wash ~ 30 d; doubles when hardness halved.
  const recommendedWashInterval_d = Math.max(7, Math.min(120, 30 * (6500 / Math.max(100, totalHardness_mgL))));

  // 3. Bromate formation risk (Br- > 0.4 mg/L is the WHO guideline floor; seawater is far above).
  const bromate_risk = p.bromide_mgL > 50 ? 'High'
    : p.bromide_mgL > 10 ? 'Moderate'
    : p.bromide_mgL > 0.4 ? 'Watch'
    : 'Low';

  // 4. Bicarbonate / alkalinity sets neutralisation duty for spent acid stream
  const neutralisation_NaOH_kg_per_event_basis = p.totalAlkalinity_mgL; // for display only

  // Daily ON hours under each duty mode:
  //   continuous → 24 h
  //   shock      → user-set shock hours per day
  //   cyclic     → 24 h × ON / (ON+OFF) with sub-minute clamp
  const cycleTotalMin = Math.max(0.5, p.cycleOnMin + p.cycleOffMin);
  const cycleDutyFrac = Math.max(0, Math.min(1, p.cycleOnMin / cycleTotalMin));
  const dailyDuty_h =
    p.dutyMode === 'continuous' ? 24
    : p.dutyMode === 'cyclic'    ? 24 * cycleDutyFrac
    : Math.max(1, Math.min(24, p.shockHoursPerDay));
  // ----------- Headline plant flows -----------
  // The user sets the hypochlorite product output directly (kg Cl2 / h)
  // and the derived free-Cl2 dose into the treated water is computed against
  // the PD-off seawater flow.
  const cl2_demand_kgh_inst = p.hypochloriteOutput_kgh;
  const cl2_demand_kgd = cl2_demand_kgh_inst * dailyDuty_h;
  const cl2_demand_kgy = cl2_demand_kgd * 365;
  const freeCl2_dose_mgL = p.seawaterFlow_PDoff_m3h > 0
    ? (p.hypochloriteOutput_kgh * 1000) / p.seawaterFlow_PDoff_m3h
    : 0;
  // Derived per-module brine flow from total electrolyser inlet
  const brineFlow_per_module_m3h = Math.max(1, p.modules_installed) > 0
    ? p.electrolyserInlet_m3h / Math.max(1, p.modules_installed)
    : p.electrolyserInlet_m3h;

  const modules_running = Math.max(1, p.modules_installed);
  const cl2_per_module_gh = (cl2_demand_kgh_inst * 1000) / modules_running;
  // Use chloride-corrected effective current efficiency derived from detailed chemistry
  const I_per_module_A    = cl2_per_module_gh / (CL2_PER_AH * (adjusted_CE_pc / 100));
  const totalCurrent_A    = I_per_module_A * modules_running;

  const cellArea_m2_per_cell = I_per_module_A / (p.currentDensity_kAm2 * 1000) / Math.max(1, p.cells_per_module);
  const cellArea_total_m2    = cellArea_m2_per_cell * p.cells_per_module * modules_running;

  const stackVoltage_V      = p.cellVoltage_V * p.cells_per_module;
  const power_per_module_kW = (stackVoltage_V * I_per_module_A) / 1000;
  const power_total_DC_kW   = power_per_module_kW * modules_running;
  // Use user-set transformer + rectifier overall efficiency (replaces old 0.95 x 0.98 product)
  const txr_eff = Math.max(0.7, Math.min(0.99, p.txr_efficiency_pc / 100));
  const power_total_AC_kW   = power_total_DC_kW / txr_eff;
  const specificEnergy_kWh_per_kgCl2 = cl2_demand_kgh_inst > 0
    ? power_total_AC_kW / cl2_demand_kgh_inst
    : 0;

  const totalBrineFlow_m3h = brineFlow_per_module_m3h * modules_running;
  const naocl_pc_w = totalBrineFlow_m3h > 0
    ? (cl2_demand_kgh_inst / (totalBrineFlow_m3h * rho / 1000)) * 100
    : 0;

  // Stoichiometry: 1 mol Cl2 -> 1 mol H2; 1 kg Cl2 -> 0.316 Nm3 H2
  // INSTANTANEOUS H2 generation = present whenever electrolysis is energised.
  // For continuous duty -> 24 h/d; for shock duty -> active only during shock window.
  // Peak instantaneous H2 still uses cl2_demand_kgh_inst (set by target dose x flow).
  const H2_Nm3h = cl2_demand_kgh_inst * 0.316;
  const dilutionAir_Nm3h = H2_Nm3h * p.vent_dilution_factor;
  const vent_H2_pc_vv = (H2_Nm3h + dilutionAir_Nm3h) > 0
    ? (H2_Nm3h / (H2_Nm3h + dilutionAir_Nm3h)) * 100
    : 0;

  // ---- BLOWER SIZING (H2 dilution) ----
  // Recommended Nm3/h = dilution air x safety factor
  const blowerFlow_Nm3h_each_duty = p.blower_duty > 0
    ? (dilutionAir_Nm3h * p.blower_safety_factor) / p.blower_duty
    : dilutionAir_Nm3h * p.blower_safety_factor;
  // Actual (working condition) m3/h: density correction Nm3 -> m3 at working T and P
  // V_actual = V_normal x (T_work_K / 273.15) x (1013 / P_work_mbar)
  const Tk = p.workingTemp_C + 273.15;
  const densityCorr = (Tk / 273.15) * (1013 / Math.max(500, p.workingPressure_mbar_atm));
  const blowerFlow_actual_m3h_each = blowerFlow_Nm3h_each_duty * densityCorr;
  const blowerFlow_total_actual_m3h = blowerFlow_actual_m3h_each * p.blower_duty;
  // Blower shaft power (adiabatic approximation, assumed cold-air blower):
  // P (kW) = (Q_actual_m3s) * dP_Pa / eta_blower  ; eta ~ 0.55 for low-pressure side-channel blowers
  const Q_m3s_each = blowerFlow_actual_m3h_each / 3600;
  const dP_Pa = p.blower_static_mbar * 100; // mbar -> Pa
  const blower_motor_kW_each = (Q_m3s_each * dP_Pa) / (0.55 * 1000);

  // ---- BLOWER TIMING ----
  // H2 is generated whenever the rectifier is energised.  Blower run schedule:
  //   continuous duty  : 24 h/d minus purge already covered
  //   shock duty       : (shockHoursPerDay + 2 x purge) hours per day
  const blower_run_hours_per_day =
    p.dutyMode === 'continuous'
      ? 24
      : p.dutyMode === 'cyclic'
        // One purge per ON-segment; cycles/day = 1440 / total cycle minutes
        ? Math.min(24, dailyDuty_h + (1440 / cycleTotalMin) * (2 * p.blower_purge_min / 60))
        : Math.min(24, p.shockHoursPerDay + 2 * (p.blower_purge_min / 60));
  // Pre-energise and post-shutdown purge for safety - re-stated as a separate value
  const blower_purge_total_min = 2 * p.blower_purge_min;

  const naocl_dose_strength_pc = Math.max(0.4, Math.min(1.0, naocl_pc_w));
  const naocl_dose_density = 1009;
  const dosingPumpFlow_total_demand_Lh = (cl2_demand_kgh_inst * 1000) / (naocl_dose_strength_pc / 100 * naocl_dose_density / 1000);
  // User-set per-duty capacity (L/h) overrides the derived demand if > 0
  const dosingPumpFlow_per_duty_Lh = p.dosingPump_capacity_Lh_each > 0
    ? p.dosingPump_capacity_Lh_each
    : (p.dosingPumps_duty > 0 ? dosingPumpFlow_total_demand_Lh / p.dosingPumps_duty : dosingPumpFlow_total_demand_Lh);
  // Installed total = per-pump capacity x duty count
  const dosingPumpFlow_total_Lh = dosingPumpFlow_per_duty_Lh * p.dosingPumps_duty;
  // Margin vs demand
  const dosing_margin_pc = dosingPumpFlow_total_demand_Lh > 0
    ? ((dosingPumpFlow_total_Lh / dosingPumpFlow_total_demand_Lh) - 1) * 100
    : 0;
  // Pump pressure = discharge head (bar -> Pa) + system static pressure (mbar -> Pa)
  const dosing_dP_Pa = p.dosingPumpHead_bar * 1e5 + p.dosingPump_staticPressure_mbar * 100;
  const dosingPump_kW_each = (dosingPumpFlow_per_duty_Lh / 1000 / 3600) * dosing_dP_Pa / 0.55 / 1000;
  const storageTank_m3 = (dosingPumpFlow_total_Lh / 1000) * p.storageTank_h;

  const acid = ACID_PROPS[p.acidType];
  const wash_volume_per_event_m3 = Math.max(0.5, p.acidWashTank_m3);
  const active_acid_kg_per_event = wash_volume_per_event_m3 * (p.acidWashConc_target_pc / 100) * 1000;
  const concentrate_m3_per_event = active_acid_kg_per_event / (acid.activeFrac * acid.sg);
  const events_per_year = 365 / Math.max(1, p.acidWashEvery_d);
  const concentrate_m3_per_year = concentrate_m3_per_event * events_per_year;
  const concentrate_kg_per_year = concentrate_m3_per_year * acid.sg;
  const acidPump_total_flow_Lh = (wash_volume_per_event_m3 * 1000) / (Math.max(1, p.acidWashDuration_min) / 60);
  const acidPump_per_duty_Lh = p.acidPumps_duty > 0
    ? acidPump_total_flow_Lh / p.acidPumps_duty
    : acidPump_total_flow_Lh;
  const acid_dP_Pa = p.acidPumpHead_bar * 1e5 + p.acidPump_staticPressure_mbar * 100;
  const acidPump_kW_each = (acidPump_per_duty_Lh / 1000 / 3600) * acid_dP_Pa / 0.55 / 1000;

  const cell_x_section_m2 = p.cells_per_module > 0
    ? (brineFlow_per_module_m3h / 3600) / Math.max(0.01, p.brineVelocity_ms)
    : 0;

  // ---- Cell-nameplate utilisation ----
  // Current / voltage utilisation = computed / rated
  const cell_current_util_pc = p.cell_ratedDC_current_A > 0
    ? (I_per_module_A / p.cell_ratedDC_current_A) * 100
    : 0;
  const cell_voltage_util_pc = p.cell_ratedDC_voltage_V > 0
    ? (stackVoltage_V / p.cell_ratedDC_voltage_V) * 100
    : 0;
  const cell_capacity_util_pc = p.cell_ratedCapacity_kgCl2h > 0
    ? ((cl2_demand_kgh_inst / modules_running) / p.cell_ratedCapacity_kgCl2h) * 100
    : 0;

  // ---- Inlet / outlet pipe velocity check ----
  const inletPipe  = lookupDN(p.cell_inlet_DN);
  const outletPipe = lookupDN(p.cell_outlet_DN);
  const inletArea_m2  = Math.PI / 4 * (inletPipe.id_mm  / 1000) ** 2;
  const outletArea_m2 = Math.PI / 4 * (outletPipe.id_mm / 1000) ** 2;
  const Q_per_module_m3s = brineFlow_per_module_m3h / 3600;
  const inlet_vel_ms  = inletArea_m2  > 0 ? Q_per_module_m3s / inletArea_m2  : 0;
  const outlet_vel_ms = outletArea_m2 > 0 ? Q_per_module_m3s / outletArea_m2 : 0;
  const recommended_inlet_DN  = recommendDN(brineFlow_per_module_m3h, 1.5);
  const recommended_outlet_DN = recommendDN(brineFlow_per_module_m3h, 1.2);

  // ---- Operating pressure band ----
  const pressure_in_band = p.cell_operatingPressure_barg >= 0.5 && p.cell_operatingPressure_barg <= 3.0;

  // ---- Calculated rated DC current (Faraday) ----
  // I_rated = (Cl2 per module x 1000) / (1.3245 g/Ah x CE) x 1.15 design margin
  // Rounded to nearest 50 A for nameplate-friendly value.
  const cl2_per_module_design_gh = (p.hypochloriteOutput_kgh * 1000) / Math.max(1, p.modules_installed);
  const rated_current_calc_A = Math.round(((cl2_per_module_design_gh / (CL2_PER_AH * (adjusted_CE_pc / 100))) * 1.15) / 50) * 50;
  // Calculated rated capacity at the user's chosen nameplate current
  const rated_capacity_calc_kgCl2h = (CL2_PER_AH * (adjusted_CE_pc / 100) * p.cell_ratedDC_current_A) / 1000;

  // ---- Dosing pump connection velocities ----
  const dosingPipeIn  = lookupDN(p.dosingPump_inlet_DN);
  const dosingPipeOut = lookupDN(p.dosingPump_outlet_DN);
  const dosing_Q_per_duty_m3s = dosingPumpFlow_per_duty_Lh / 1000 / 3600;
  const dosing_in_vel_ms  = dosing_Q_per_duty_m3s / (Math.PI / 4 * (dosingPipeIn.id_mm  / 1000) ** 2);
  const dosing_out_vel_ms = dosing_Q_per_duty_m3s / (Math.PI / 4 * (dosingPipeOut.id_mm / 1000) ** 2);
  const recommended_dosing_in_DN  = recommendDN(dosingPumpFlow_per_duty_Lh / 1000, 0.5);
  const recommended_dosing_out_DN = recommendDN(dosingPumpFlow_per_duty_Lh / 1000, 1.0);

  // ---- Blower connection velocities (uses ACTUAL m3/h at working condition) ----
  const blowerPipeIn  = lookupDN(p.blower_inlet_DN);
  const blowerPipeOut = lookupDN(p.blower_outlet_DN);
  const blower_Q_each_m3s = blowerFlow_actual_m3h_each / 3600;
  const blower_in_vel_ms  = blower_Q_each_m3s / (Math.PI / 4 * (blowerPipeIn.id_mm  / 1000) ** 2);
  const blower_out_vel_ms = blower_Q_each_m3s / (Math.PI / 4 * (blowerPipeOut.id_mm / 1000) ** 2);
  const recommended_blower_in_DN  = recommendDN(blowerFlow_actual_m3h_each, 12);   // air intake 8 - 15 m/s
  const recommended_blower_out_DN = recommendDN(blowerFlow_actual_m3h_each, 10);   // discharge 8 - 12 m/s

  // ---- Acid pump connection velocities ----
  const acidPipeIn  = lookupDN(p.acidPump_inlet_DN);
  const acidPipeOut = lookupDN(p.acidPump_outlet_DN);
  const acid_Q_per_duty_m3s = acidPump_per_duty_Lh / 1000 / 3600;
  const acid_in_vel_ms  = acid_Q_per_duty_m3s / (Math.PI / 4 * (acidPipeIn.id_mm  / 1000) ** 2);
  const acid_out_vel_ms = acid_Q_per_duty_m3s / (Math.PI / 4 * (acidPipeOut.id_mm / 1000) ** 2);
  const recommended_acid_in_DN  = recommendDN(acidPump_per_duty_Lh / 1000, 0.5);
  const recommended_acid_out_DN = recommendDN(acidPump_per_duty_Lh / 1000, 1.0);

  // ---- Seawater pre-filter sizing (everything derived from PD-off inflow) ----
  const filter_flow_m3h = p.seawaterFlow_PDoff_m3h; // sized to MAX plant flow
  const filter_area_m2  = filter_flow_m3h / Math.max(1, p.filter_rate_m3_m2_h);
  // Velocity at inlet / outlet on chosen DN
  const filterPipeIn  = lookupDN(p.filter_inlet_DN);
  const filterPipeOut = lookupDN(p.filter_outlet_DN);
  const filter_in_vel  = (filter_flow_m3h / 3600) / (Math.PI / 4 * (filterPipeIn.id_mm  / 1000) ** 2);
  const filter_out_vel = (filter_flow_m3h / 3600) / (Math.PI / 4 * (filterPipeOut.id_mm / 1000) ** 2);
  // Recommended pipe DN at 2 m/s seawater design velocity
  const recommended_filter_in_DN  = recommendDN(filter_flow_m3h, 2.0);
  const recommended_filter_out_DN = recommendDN(filter_flow_m3h, 2.0);
  // Backwash flow envelope: typically 2 - 5 % of forward flow per cycle
  const filter_backwash_m3h = filter_flow_m3h * 0.04;
  // Number of filters: 1 duty + 1 standby (matches preset)
  const filter_units_duty    = 1;
  const filter_units_standby = 1;

  // ---- Eductor connection velocities (standard motive/suction/discharge) ----
  const eductorMotivePipe    = lookupDN(p.eductor_motive_DN);
  const eductorSuctionPipe   = lookupDN(p.eductor_suction_DN);
  const eductorDischargePipe = lookupDN(p.eductor_discharge_DN);
  const eductor_motive_vel    = (p.eductor_motive_m3h / 3600)        / (Math.PI / 4 * (eductorMotivePipe.id_mm    / 1000) ** 2);
  const eductor_suction_vel   = (p.eductor_suction_Lh / 1000 / 3600) / (Math.PI / 4 * (eductorSuctionPipe.id_mm   / 1000) ** 2);
  const eductor_discharge_m3h = p.eductor_motive_m3h + p.eductor_suction_Lh / 1000;
  const eductor_discharge_vel = (eductor_discharge_m3h / 3600)       / (Math.PI / 4 * (eductorDischargePipe.id_mm / 1000) ** 2);
  // Motive : suction ratio - typical eductor envelope 4 : 1 to 8 : 1
  const eductor_ratio = p.eductor_suction_Lh > 0
    ? p.eductor_motive_m3h / (p.eductor_suction_Lh / 1000)
    : 0;
  const eductor_ratio_ok = eductor_ratio >= 3 && eductor_ratio <= 10;

  // ---- Tank gas-space (degassing residence + freeboard) ----
  const storage_gas_volume_m3 = p.storage_tank_volume_m3 * (p.storage_freeboard_pc / 100);
  // Degassing-tank H2 disengagement: actual residence available vs required
  const storage_actual_residence_min = (p.storage_tank_volume_m3 / Math.max(0.01, p.electrolyserInlet_m3h)) * 60;
  const storage_degassing_ok = storage_actual_residence_min >= p.storage_degassing_residence_min;

  const ce_in_band = p.currentEfficiency_pc >= 85 && p.currentEfficiency_pc <= 95;
  const cd_in_band = p.currentDensity_kAm2 >= 2.0 && p.currentDensity_kAm2 <= 4.0;
  const v_in_band  = p.brineVelocity_ms >= 0.4 && p.brineVelocity_ms <= 0.8;
  const vent_safe  = vent_H2_pc_vv <= 1.0;
  const dilution_ok = p.vent_dilution_factor >= 30;
  const temp_in_band = p.temperature_C >= 10 && p.temperature_C <= 35;
  const sal_in_band  = p.salinity_ppt  >= 30 && p.salinity_ppt  <= 40;

  return {
    rho, cond,
    freeCl2_dose_mgL, brineFlow_per_module_m3h,
    adjusted_CE_pc, Cl_factor, totalHardness_mgL, recommendedWashInterval_d,
    bromate_risk, neutralisation_NaOH_kg_per_event_basis,
    cl2_demand_kgh: cl2_demand_kgh_inst, cl2_demand_kgd, cl2_demand_kgy,
    I_per_module_A, totalCurrent_A,
    cellArea_m2_per_cell, cellArea_total_m2,
    stackVoltage_V, power_per_module_kW, power_total_DC_kW, power_total_AC_kW,
    specificEnergy_kWh_per_kgCl2,
    naocl_pc_w, totalBrineFlow_m3h,
    H2_Nm3h, dilutionAir_Nm3h, vent_H2_pc_vv,
    blowerFlow_Nm3h_each_duty, blowerFlow_actual_m3h_each, blowerFlow_total_actual_m3h,
    blower_motor_kW_each, blower_run_hours_per_day, blower_purge_total_min,
    densityCorr,
    dosingPumpFlow_total_Lh, dosingPumpFlow_per_duty_Lh, dosingPump_kW_each,
    storageTank_m3,
    wash_volume_per_event_m3, active_acid_kg_per_event,
    concentrate_m3_per_event, concentrate_m3_per_year, concentrate_kg_per_year,
    events_per_year,
    acidPump_total_flow_Lh, acidPump_per_duty_Lh, acidPump_kW_each,
    cell_x_section_m2,
    cell_current_util_pc, cell_voltage_util_pc, cell_capacity_util_pc,
    rated_current_calc_A, rated_capacity_calc_kgCl2h,
    inletPipe, outletPipe, inlet_vel_ms, outlet_vel_ms,
    recommended_inlet_DN, recommended_outlet_DN, pressure_in_band,
    dosingPumpFlow_total_demand_Lh, dosing_margin_pc,
    dosing_in_vel_ms, dosing_out_vel_ms, recommended_dosing_in_DN, recommended_dosing_out_DN,
    blower_in_vel_ms, blower_out_vel_ms, recommended_blower_in_DN, recommended_blower_out_DN,
    acid_in_vel_ms, acid_out_vel_ms, recommended_acid_in_DN, recommended_acid_out_DN,
    filter_flow_m3h, filter_area_m2, filter_in_vel, filter_out_vel,
    recommended_filter_in_DN, recommended_filter_out_DN,
    filter_backwash_m3h, filter_units_duty, filter_units_standby,
    eductor_motive_vel, eductor_suction_vel, eductor_discharge_vel,
    eductor_discharge_m3h, eductor_ratio, eductor_ratio_ok,
    storage_gas_volume_m3, storage_actual_residence_min, storage_degassing_ok,
    txr_eff,
    ce_in_band, cd_in_band, v_in_band, vent_safe, dilution_ok,
    temp_in_band, sal_in_band, dailyDuty_h,
  };
}

type Crit = { label: string; value: string; limit: string; pass: boolean; critical?: boolean };

function validate(p: ECParams, r: ReturnType<typeof calcResults>): Crit[] {
  return [
    { label: 'Seawater salinity',          value: `${p.salinity_ppt.toFixed(1)} ppt`,            limit: '30 - 40 ppt',           pass: r.sal_in_band, critical: true },
    { label: 'Seawater temperature',       value: `${p.temperature_C.toFixed(1)} C`,             limit: '10 - 35 C',             pass: r.temp_in_band, critical: true },
    { label: 'Current density',            value: `${p.currentDensity_kAm2.toFixed(2)} kA/m^2`,  limit: '2.0 - 4.0',             pass: r.cd_in_band, critical: true },
    { label: 'Cell voltage',               value: `${p.cellVoltage_V.toFixed(2)} V`,             limit: '3.5 - 4.5 V',           pass: p.cellVoltage_V >= 3.5 && p.cellVoltage_V <= 4.5 },
    { label: 'Current efficiency',         value: `${p.currentEfficiency_pc.toFixed(0)} %`,      limit: '85 - 95 %',             pass: r.ce_in_band, critical: true },
    { label: 'Brine velocity',             value: `${p.brineVelocity_ms.toFixed(2)} m/s`,        limit: '0.4 - 0.8 m/s',         pass: r.v_in_band, critical: true },
    { label: 'NaOCl product strength',     value: `${r.naocl_pc_w.toFixed(2)} % w/w`,            limit: '0.4 - 0.8 %',           pass: r.naocl_pc_w >= 0.4 && r.naocl_pc_w <= 1.0 },
    { label: 'Vent H2 fraction',           value: `${r.vent_H2_pc_vv.toFixed(2)} % v/v`,         limit: '< 1.0 % (25 % of LEL)', pass: r.vent_safe, critical: true },
    { label: 'H2 dilution ratio',          value: `${p.vent_dilution_factor.toFixed(0)} : 1`,    limit: '>= 30 : 1',             pass: r.dilution_ok, critical: true },
    { label: 'Modules redundancy',         value: `${p.modules_installed} + ${p.modules_standby}`, limit: '>= 1 standby',        pass: p.modules_standby >= 1 },
    { label: 'Dosing pumps redundancy',    value: `${p.dosingPumps_duty} + ${p.dosingPumps_standby}`, limit: '>= 1 standby',     pass: p.dosingPumps_standby >= 1 },
    { label: 'Acid pumps redundancy',      value: `${p.acidPumps_duty} + ${p.acidPumps_standby}`, limit: '>= 1 standby',         pass: p.acidPumps_standby >= 1 },
    { label: 'H2 dilution blowers redundancy', value: `${p.blower_duty} + ${p.blower_standby}`,  limit: '>= 1 standby',          pass: p.blower_standby >= 1, critical: true },
    { label: 'Blower safety factor',       value: `x ${p.blower_safety_factor.toFixed(2)}`,      limit: '1.1 - 1.5',             pass: p.blower_safety_factor >= 1.1 && p.blower_safety_factor <= 1.5 },
    { label: 'Working temperature',        value: `${p.workingTemp_C.toFixed(1)} C`,             limit: '5 - 45 C',              pass: p.workingTemp_C >= 5 && p.workingTemp_C <= 45 },
    { label: 'Blower static pressure',     value: `${p.blower_static_mbar.toFixed(0)} mbar`,     limit: '20 - 200 mbar',         pass: p.blower_static_mbar >= 20 && p.blower_static_mbar <= 200 },
    { label: 'Cell current utilisation',   value: `${r.cell_current_util_pc.toFixed(0)} %`,      limit: '60 - 100 % of rated',   pass: r.cell_current_util_pc >= 60 && r.cell_current_util_pc <= 105, critical: true },
    { label: 'Cell voltage utilisation',   value: `${r.cell_voltage_util_pc.toFixed(0)} %`,      limit: '70 - 105 % of rated',   pass: r.cell_voltage_util_pc >= 70 && r.cell_voltage_util_pc <= 105 },
    { label: 'Cell capacity utilisation',  value: `${r.cell_capacity_util_pc.toFixed(0)} %`,     limit: '50 - 100 % of rated',   pass: r.cell_capacity_util_pc >= 50 && r.cell_capacity_util_pc <= 105, critical: true },
    { label: 'Operating pressure',         value: `${p.cell_operatingPressure_barg.toFixed(2)} barg`, limit: '0.5 - 3.0 barg',   pass: r.pressure_in_band, critical: true },
    { label: 'Inlet pipe velocity',        value: `${r.inlet_vel_ms.toFixed(2)} m/s`,            limit: '0.4 - 1.5 m/s',         pass: r.inlet_vel_ms >= 0.4 && r.inlet_vel_ms <= 1.5 },
    { label: 'Outlet pipe velocity',       value: `${r.outlet_vel_ms.toFixed(2)} m/s`,           limit: '0.3 - 1.2 m/s',         pass: r.outlet_vel_ms >= 0.3 && r.outlet_vel_ms <= 1.2 },
    { label: 'NaOCl storage residence',    value: `${p.storageTank_h.toFixed(1)} h`,             limit: '1 - 4 h',               pass: p.storageTank_h >= 1 && p.storageTank_h <= 4 },
    { label: 'Acid wash interval',         value: `every ${p.acidWashEvery_d} d`,                limit: '14 - 60 d',             pass: p.acidWashEvery_d >= 14 && p.acidWashEvery_d <= 60 },
    { label: 'Acid wash concentration',    value: `${p.acidWashConc_target_pc.toFixed(1)} % w/w`,limit: '3 - 10 %',              pass: p.acidWashConc_target_pc >= 3 && p.acidWashConc_target_pc <= 10 },
  ];
}

// ---------------------------------------------------------------------------
// Small UI helpers
// ---------------------------------------------------------------------------
function NumField(props: { label: string; value: number; step?: number; min?: number; max?: number; suffix?: string; onChange: (v: number) => void; help?: string; }) {
  const hasRange = props.min !== undefined && props.max !== undefined;
  const v = Number.isFinite(props.value) ? props.value : 0;
  return (
    <label className="block">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium text-slate-300">{props.label}</span>
        <span className="text-[10px] text-slate-500">
          {props.suffix && <span>{props.suffix} </span>}
          {hasRange && <span className="text-slate-600">[{props.min}-{props.max}]</span>}
        </span>
      </div>
      <div className="flex items-center gap-2">
        {hasRange && (
          <input
            type="range"
            value={v}
            step={props.step ?? 0.1}
            min={props.min}
            max={props.max}
            onChange={e => props.onChange(parseFloat(e.target.value) || 0)}
            className="flex-1 accent-cyan-500 h-1.5"
          />
        )}
        <input
          type="number"
          value={v}
          step={props.step ?? 0.1}
          min={props.min}
          max={props.max}
          onChange={e => props.onChange(parseFloat(e.target.value) || 0)}
          className={`${hasRange ? 'w-20' : 'w-full'} px-2 py-1.5 rounded-md bg-slate-900/60 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:border-cyan-500`}
        />
      </div>
      {props.help && <p className="text-[10px] text-slate-500 mt-0.5">{props.help}</p>}
    </label>
  );
}

function SelField<T extends string>(props: { label: string; value: T; options: { value: T; label: string }[]; onChange: (v: T) => void; }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-300 mb-1 block">{props.label}</span>
      <select
        value={props.value}
        onChange={e => props.onChange(e.target.value as T)}
        className="w-full px-3 py-1.5 rounded-md bg-slate-900/60 border border-slate-700 text-slate-100 text-sm focus:outline-none focus:border-cyan-500"
      >
        {props.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

function Section({ title, icon, children, action }: { title: string; icon: React.ReactNode; children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <section className="rounded-xl bg-slate-900/40 border border-slate-700/60 p-4">
      <div className="flex items-center justify-between mb-3 gap-2">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-cyan-300">{icon}{title}</h3>
        {action}
      </div>
      {children}
    </section>
  );
}

function RecommendBtn({ onClick, label = 'Recommend' }: { onClick: () => void; label?: string }) {
  return (
    <button onClick={onClick}
      className="px-2 py-1 rounded-md text-[10.5px] font-semibold bg-cyan-600/20 border border-cyan-500/60 text-cyan-200 hover:bg-cyan-600/30 transition-colors inline-flex items-center gap-1">
      <Activity className="w-3 h-3" /> {label}
    </button>
  );
}

function KPI({ label, value, unit, tone = 'neutral' }: { label: string; value: string | number; unit?: string; tone?: 'neutral' | 'good' | 'warn' | 'bad'; }) {
  const toneCls = tone === 'good' ? 'border-emerald-500/40 text-emerald-300'
    : tone === 'warn' ? 'border-amber-500/40 text-amber-300'
    : tone === 'bad' ? 'border-rose-500/40 text-rose-300'
    : 'border-slate-600 text-slate-200';
  return (
    <div className={`rounded-lg bg-slate-900/50 border ${toneCls} p-3`}>
      <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-0.5">{label}</div>
      <div className="text-lg font-semibold">{value}{unit && <span className="text-xs text-slate-400 ml-1">{unit}</span>}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Process flow diagram (SVG, live values)
// ---------------------------------------------------------------------------
function ProcessDiagram({ p, r }: { p: ECParams; r: ReturnType<typeof calcResults> }) {
  // Stream values
  const ventTone = r.vent_safe ? '#10b981' : '#f43f5e';
  const energyTone = r.specificEnergy_kWh_per_kgCl2 < 5.5 ? '#10b981' : r.specificEnergy_kWh_per_kgCl2 < 7 ? '#f59e0b' : '#f43f5e';

  // Helper for boxes — larger title, larger body, more vertical spacing per line
  const Box = ({ x, y, w, h, title, lines, fill = '#0f172a', stroke = '#475569' }: {
    x: number; y: number; w: number; h: number; title: string; lines: string[]; fill?: string; stroke?: string;
  }) => (
    <g>
      <rect x={x} y={y} width={w} height={h} rx={8} fill={fill} stroke={stroke} strokeWidth={1.8} />
      <rect x={x} y={y} width={w} height={20} rx={8} fill={stroke} fillOpacity={0.18} />
      <text x={x + w / 2} y={y + 14} fill={stroke} fontSize={13} fontWeight={700} textAnchor="middle" letterSpacing={0.4}>{title}</text>
      {lines.map((l, i) => (
        <text key={i} x={x + w / 2} y={y + 36 + i * 14} fill="#e2e8f0" fontSize={12} textAnchor="middle">{l}</text>
      ))}
    </g>
  );

  const Arrow = ({ x1, y1, x2, y2, label, colour = '#64748b', dashed = false }: {
    x1: number; y1: number; x2: number; y2: number; label?: string; colour?: string; dashed?: boolean;
  }) => {
    const midX = (x1 + x2) / 2;
    const midY = (y1 + y2) / 2;
    return (
      <g>
        <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={colour} strokeWidth={2.2}
          markerEnd="url(#arrow)" strokeDasharray={dashed ? '6 4' : undefined} />
        {label && (
          <text x={midX} y={midY - 5} fill={colour} fontSize={12} fontWeight={700} textAnchor="middle">{label}</text>
        )}
      </g>
    );
  };

  return (
    <div className="w-full overflow-x-auto">
      <svg viewBox="0 0 1400 640" className="w-full h-auto" style={{ minWidth: 900 }}
           preserveAspectRatio="xMidYMid meet">
        <defs>
          <marker id="arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <path d="M 0 0 L 10 5 L 0 10 z" fill="#64748b" />
          </marker>
          <linearGradient id="brineGrad" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor="#0ea5e9" />
            <stop offset="100%" stopColor="#22d3ee" />
          </linearGradient>
        </defs>

        {/* Title strip */}
        <text x={700} y={26} fill="#cbd5e1" fontSize={16} fontWeight={700} textAnchor="middle" letterSpacing={2}>
          SEAWATER ELECTROCHLORINATION — LIVE PROCESS TRAIN
        </text>
        <text x={700} y={48} fill="#64748b" fontSize={12} textAnchor="middle" letterSpacing={1}>
          Driven by Cl₂ production target · all stages re-size in real time
        </text>
        {/* Apply a global transform to spread the existing layout out to fill the new viewBox */}
        <g transform="translate(40, 80) scale(1.32)">

        {/* 1. Seawater intake */}
        <Box x={10}  y={120} w={130} h={68} title="Seawater intake"
          lines={[`PD-off ${p.seawaterFlow_PDoff_m3h.toFixed(2)} m3/h`, `electrolyser ${p.electrolyserInlet_m3h.toFixed(2)} m3/h`, `${p.salinity_ppt.toFixed(1)} ppt, ${p.temperature_C.toFixed(1)} C`]}
          fill="#082f49" stroke="#0ea5e9" />

        {/* 2. Seawater pre-filter */}
        <Box x={160} y={120} w={110} h={72} title="Seawater filter"
          lines={[
            `${r.filter_flow_m3h.toFixed(1)} m3/h`,
            `${p.filter_micron_um} um, ${p.filter_designDP_bar.toFixed(2)} bar DP`,
            `${r.filter_units_duty}+${r.filter_units_standby} units`,
            `in DN${p.filter_inlet_DN} / out DN${p.filter_outlet_DN}`,
          ]} fill="#0f172a" stroke="#38bdf8" />

        {/* 3. Electrolyser stack */}
        <Box x={285} y={110} w={170} h={108} title={`Electrolyser stack`}
          lines={[
            `${p.modules_installed} duty + ${p.modules_standby} standby`,
            `${p.cells_per_module} cells, ${p.currentDensity_kAm2.toFixed(1)} kA/m2`,
            `${r.stackVoltage_V.toFixed(0)} V x ${r.I_per_module_A.toFixed(0)} A`,
            `${r.power_total_AC_kW.toFixed(1)} kW AC`,
            `P_op ${p.cell_operatingPressure_barg.toFixed(2)} barg`,
            `in DN${p.cell_inlet_DN} / out DN${p.cell_outlet_DN}`,
          ]} fill="#082f49" stroke="#22d3ee" />

        {/* Rectifier on top */}
        <Box x={310} y={22}  w={120} h={56} title="DC rectifier"
          lines={[`${r.power_total_DC_kW.toFixed(1)} kW DC`, `eff. 0.95 / 0.98`]} fill="#1f1d3a" stroke="#a78bfa" />
        <Arrow x1={370} y1={78} x2={370} y2={110} label="DC" colour="#a78bfa" />

        {/* 4. Gas-liquid separator */}
        <Box x={475} y={110} w={130} h={92} title="Gas-liquid separator"
          lines={[`NaOCl ${r.naocl_pc_w.toFixed(2)} % w/w`, `+ H2 vent`, `+ residual brine`]} fill="#0f172a" />

        {/* 5. NaOCl storage */}
        <Box x={625} y={120} w={130} h={72} title="NaOCl storage"
          lines={[`${r.storageTank_m3.toFixed(2)} m3`, `${p.storageTank_h.toFixed(1)} h residence`, `0.4 - 0.8 % w/w`]} fill="#062c1e" stroke="#10b981" />

        {/* 6. Dosing pumps */}
        <Box x={775} y={120} w={130} h={90} title="Dosing pumps"
          lines={[
            `${p.dosingPumps_duty} duty + ${p.dosingPumps_standby} standby`,
            `${r.dosingPumpFlow_per_duty_Lh.toFixed(0)} L/h each`,
            `${p.dosingPumpHead_bar.toFixed(1)} bar @ ${r.dosingPump_kW_each.toFixed(2)} kW`,
            `in DN${p.dosingPump_inlet_DN} / out DN${p.dosingPump_outlet_DN}`,
          ]} fill="#062c1e" stroke="#10b981" />

        {/* 7. Injection point */}
        <Box x={830} y={234} w={120} h={78} title="Eductor + injection"
          lines={[
            `motive ${p.eductor_motive_m3h.toFixed(1)} m3/h`,
            `suction ${p.eductor_suction_Lh.toFixed(0)} L/h`,
            `free Cl2 ${r.freeCl2_dose_mgL.toFixed(2)} mg/L`,
            `discharge DN${p.eductor_discharge_DN}`,
          ]} fill="#0f172a" stroke="#22d3ee" />

        {/* 8. H2 vent + blower */}
        <Box x={475} y={14} w={170} h={84}
          title="H2 vent + dilution blowers"
          lines={[
            `H2 ${r.H2_Nm3h.toFixed(2)} Nm3/h`,
            `blower ${r.blowerFlow_Nm3h_each_duty.toFixed(0)} Nm3/h ea (${p.blower_duty}+${p.blower_standby})`,
            `static ${p.blower_static_mbar} mbar / ${r.blower_motor_kW_each.toFixed(2)} kW`,
            `intake DN${p.blower_inlet_DN} / vent DN${p.blower_outlet_DN}`,
          ]} fill="#3a0f1a" stroke={ventTone} />
        <text x={560} y={106} fill={ventTone} fontSize={9} fontWeight={700} textAnchor="middle">
          vent [H2] {r.vent_H2_pc_vv.toFixed(2)} % v/v
        </text>

        {/* 9. Acid wash (CIP) loop */}
        <Box x={285} y={300} w={170} h={92} title="Acid wash / CIP"
          lines={[
            `${p.acidType.replace('_', ' ')}`,
            `every ${p.acidWashEvery_d} d, ${p.acidWashDuration_min} min`,
            `${p.acidWashConc_target_pc.toFixed(1)} % w/w; tank ${p.acidWashTank_m3.toFixed(1)} m3`,
            `pump ${r.acidPump_per_duty_Lh.toFixed(0)} L/h @ ${p.acidPumpHead_bar.toFixed(1)} bar`,
          ]} fill="#451a03" stroke="#f59e0b" />

        {/* 10. Neutralisation tank */}
        <Box x={475} y={310} w={130} h={72} title="Neutralisation"
          lines={[`spent acid`, `pH adjust to 6 - 9`, `${r.concentrate_kg_per_year.toFixed(0)} kg/yr acid`]} fill="#0f172a" stroke="#f59e0b" />

        {/* Working conditions banner */}
        <g>
          <rect x={625} y={310} width={310} height={72} rx={6} fill="#0f172a" stroke="#64748b" strokeWidth={1.2} />
          <text x={780} y={325} fill="#94a3b8" fontSize={10.5} fontWeight={700} textAnchor="middle">Working conditions (all devices)</text>
          <text x={640} y={345} fill="#e2e8f0" fontSize={10}>T_work = {p.workingTemp_C.toFixed(1)} C</text>
          <text x={640} y={358} fill="#e2e8f0" fontSize={10}>P_atm  = {p.workingPressure_mbar_atm.toFixed(0)} mbar</text>
          <text x={780} y={345} fill="#e2e8f0" fontSize={10}>density corr. = {r.densityCorr.toFixed(3)}</text>
          <text x={780} y={358} fill="#e2e8f0" fontSize={10}>spec. energy = {r.specificEnergy_kWh_per_kgCl2.toFixed(2)} kWh/kg Cl2</text>
          <circle cx={920} cy={350} r={6} fill={energyTone} />
        </g>

        {/* Streams */}
        <Arrow x1={140} y1={154} x2={160} y2={154} colour="#0ea5e9" />
        <Arrow x1={260} y1={154} x2={285} y2={156} label={`${p.electrolyserInlet_m3h.toFixed(1)} m3/h`} colour="#0ea5e9" />
        <Arrow x1={455} y1={156} x2={475} y2={156} label="brine" colour="#22d3ee" />
        <Arrow x1={605} y1={156} x2={625} y2={156} label="NaOCl" colour="#10b981" />
        <Arrow x1={755} y1={156} x2={775} y2={156} colour="#10b981" />
        <Arrow x1={840} y1={192} x2={870} y2={234} label={`${r.dosingPumpFlow_total_Lh.toFixed(0)} L/h`} colour="#10b981" />

        {/* H2 path (separator -> blower) */}
        <Arrow x1={540} y1={110} x2={540} y2={90}  label="H2 + air" colour={ventTone} />

        {/* Acid wash loop (CIP <-> electrolyser) */}
        <Arrow x1={370} y1={300} x2={370} y2={202} label="acid in" colour="#f59e0b" dashed />
        <Arrow x1={400} y1={202} x2={400} y2={300} label="spent" colour="#f59e0b" dashed />
        <Arrow x1={455} y1={344} x2={475} y2={344} colour="#f59e0b" dashed />

        {/* Bottom legend */}
        <g>
          <rect x={10} y={414} width={960} height={36} rx={5} fill="#0f172a" stroke="#334155" />
          <circle cx={28} cy={432} r={5} fill="#0ea5e9" />
          <text x={40} y={436} fill="#94a3b8" fontSize={10}>seawater</text>
          <circle cx={120} cy={432} r={5} fill="#22d3ee" />
          <text x={132} y={436} fill="#94a3b8" fontSize={10}>brine</text>
          <circle cx={195} cy={432} r={5} fill="#10b981" />
          <text x={207} y={436} fill="#94a3b8" fontSize={10}>hypochlorite</text>
          <circle cx={310} cy={432} r={5} fill={ventTone} />
          <text x={322} y={436} fill="#94a3b8" fontSize={10}>H2 / vent air</text>
          <circle cx={430} cy={432} r={5} fill="#f59e0b" />
          <text x={442} y={436} fill="#94a3b8" fontSize={10}>acid wash (intermittent)</text>
          <circle cx={620} cy={432} r={5} fill="#a78bfa" />
          <text x={632} y={436} fill="#94a3b8" fontSize={10}>DC power</text>
          <text x={950} y={436} fill="#64748b" fontSize={9} textAnchor="end">live - updates with inputs</text>
        </g>
        </g>{/* close global scale transform */}
      </svg>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
type SystemPreset = 'oneOne' | 'twoOne' | 'threeOne' | 'custom';
type TopTab       = 'process' | 'chemistry' | 'config';

function applySystemPreset(p: ECParams, preset: SystemPreset): ECParams {
  switch (preset) {
    case 'oneOne':
      return { ...p, modules_installed: 1, modules_standby: 1,
        dosingPumps_duty: 1, dosingPumps_standby: 1,
        blower_duty: 1,      blower_standby: 1,
        acidPumps_duty: 1,   acidPumps_standby: 1 };
    case 'twoOne':
      return { ...p, modules_installed: 2, modules_standby: 1,
        dosingPumps_duty: 2, dosingPumps_standby: 1,
        blower_duty: 2,      blower_standby: 1,
        acidPumps_duty: 1,   acidPumps_standby: 1 };
    case 'threeOne':
      return { ...p, modules_installed: 3, modules_standby: 1,
        dosingPumps_duty: 3, dosingPumps_standby: 1,
        blower_duty: 2,      blower_standby: 1,
        acidPumps_duty: 1,   acidPumps_standby: 1 };
    default:
      return p;
  }
}

export default function ElectrochlorinationSpecPage() {
  const [p, setP] = useState<ECParams>(DEFAULTS);
  const [tab, setTab] = useState<TopTab>('process');
  const [preset, setPreset] = useState<SystemPreset>('oneOne');
  const r = useMemo(() => calcResults(p), [p]);
  const checks = useMemo(() => validate(p, r), [p, r]);
  const passCount = checks.filter(c => c.pass).length;
  const totalChecks = checks.length;

  // ─── Cloud config save/load ────────────────────────────
  const [configName, setConfigName] = useState('EC config');
  const [cloudConfigId, setCloudConfigId] = useState<number | null>(null);
  const [cloudSaving, setCloudSaving] = useState(false);
  const [cloudPickerOpen, setCloudPickerOpen] = useState(false);
  const [cloudList, setCloudList] = useState<any[]>([]);
  const [cloudLoading, setCloudLoading] = useState(false);

  const handleCloudSave = async () => {
    setCloudSaving(true);
    try {
      const { apiSaveECConfig } = await import('@/lib/phpApi');
      const res = await apiSaveECConfig({
        id: cloudConfigId ?? undefined,
        name: configName.trim() || 'EC config',
        cl2_kgh: p.hypochloriteOutput_kgh,
        seawater_m3h: p.seawaterFlow_PDoff_m3h,
        modules: p.modules_installed,
        spec: p,
      });
      setCloudConfigId(res.id);
      (await import('sonner')).toast.success(`Saved to cloud (id ${res.id})`);
    } catch (e: any) {
      (await import('sonner')).toast.error(`Save failed: ${e?.message ?? e}`);
    } finally { setCloudSaving(false); }
  };
  const handleCloudLoadOpen = async () => {
    setCloudPickerOpen(true);
    setCloudLoading(true);
    try {
      const { apiListECConfigs } = await import('@/lib/phpApi');
      setCloudList(await apiListECConfigs());
    } catch (e: any) {
      (await import('sonner')).toast.error(`List failed: ${e?.message ?? e}`);
    } finally { setCloudLoading(false); }
  };
  const handleCloudLoad = async (id: number) => {
    try {
      const { apiGetECConfig } = await import('@/lib/phpApi');
      const wb = await apiGetECConfig(id);
      if (wb.spec) setP(wb.spec as ECParams);
      setCloudConfigId(wb.id);
      setConfigName(wb.name);
      setCloudPickerOpen(false);
      (await import('sonner')).toast.success(`Loaded "${wb.name}"`);
    } catch (e: any) {
      (await import('sonner')).toast.error(`Load failed: ${e?.message ?? e}`);
    }
  };
  const handleCloudDelete = async (id: number, name: string) => {
    if (!confirm(`Delete "${name}"?`)) return;
    try {
      const { apiDeleteECConfig, apiListECConfigs } = await import('@/lib/phpApi');
      await apiDeleteECConfig(id);
      setCloudList(await apiListECConfigs());
      if (cloudConfigId === id) setCloudConfigId(null);
    } catch (e: any) {
      (await import('sonner')).toast.error(`Delete failed: ${e?.message ?? e}`);
    }
  };

  const choosePreset = (next: SystemPreset) => {
    setPreset(next);
    if (next !== 'custom') setP(prev => applySystemPreset(prev, next));
  };

  // Detect manual divergence from the active preset and downgrade to 'custom'
  useEffect(() => {
    if (preset === 'custom') return;
    const ideal = applySystemPreset(p, preset);
    const drift = ideal.modules_installed !== p.modules_installed
      || ideal.modules_standby !== p.modules_standby
      || ideal.dosingPumps_duty !== p.dosingPumps_duty
      || ideal.dosingPumps_standby !== p.dosingPumps_standby
      || ideal.blower_duty !== p.blower_duty
      || ideal.blower_standby !== p.blower_standby
      || ideal.acidPumps_duty !== p.acidPumps_duty
      || ideal.acidPumps_standby !== p.acidPumps_standby;
    if (drift) setPreset('custom');
  }, [p, preset]);

  const sweepSal = useMemo(() => {
    const out: { salinity: number; specEnergy: number; cond: number }[] = [];
    for (let s = 25; s <= 40; s += 1) {
      const rr = calcResults({ ...p, salinity_ppt: s });
      out.push({ salinity: s, specEnergy: +rr.specificEnergy_kWh_per_kgCl2.toFixed(2), cond: +rr.cond.toFixed(1) });
    }
    return out;
  }, [p]);

  const sweepT = useMemo(() => {
    const out: { temp: number; specEnergy: number }[] = [];
    for (let t = 5; t <= 35; t += 2) {
      const rr = calcResults({ ...p, temperature_C: t });
      out.push({ temp: t, specEnergy: +rr.specificEnergy_kWh_per_kgCl2.toFixed(2) });
    }
    return out;
  }, [p]);

  return (
    <div className="p-4 md:p-6 space-y-6">
      <PageHeader
        title="Electrochlorination - sizing & specification"
        subtitle="On-site sodium hypochlorite generation from seawater. Standard seawater properties (salinity 30 - 40 ppt, 0 - 35 deg C) embedded; user-adjustable inputs for dosing pumps, acid washing / CIP, cell stack, and N+1 redundancy."
        icon={Zap}
        actions={
          <div className="flex gap-2">
            <button onClick={handleCloudSave} disabled={cloudSaving}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-purple-500/10 text-purple-300 text-xs font-bold border border-purple-500/30 hover:bg-purple-500/20 disabled:opacity-50">
              ☁ {cloudSaving ? 'Saving…' : cloudConfigId ? `Update cloud (#${cloudConfigId})` : 'Save to cloud'}
            </button>
            <button onClick={handleCloudLoadOpen}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-sky-500/10 text-sky-300 text-xs font-bold border border-sky-500/30 hover:bg-sky-500/20">
              📂 Open from cloud
            </button>
          </div>
        }
      />

      {/* ─── PRODUCTION DRIVER PANEL — Cl₂ kg/h sets the cell + everything else ─── */}
      <div className="p-4 rb-card border-l-4 border-emerald-500/60">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
          <div className="md:col-span-3">
            <p className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold">PRODUCTION DRIVER</p>
            <p className="text-[11px] text-slate-400">Set Cl₂ output here and the whole spec re-sizes</p>
          </div>
          <div className="md:col-span-5">
            <label className="text-[10px] uppercase text-slate-500">Liquid Cl₂ production (NaOCl as Cl₂)</label>
            <div className="flex items-center gap-3">
              <input type="range" min={0.1} max={500} step={0.5} value={p.hypochloriteOutput_kgh}
                onChange={e => setP(prev => ({ ...prev, hypochloriteOutput_kgh: Number(e.target.value) }))}
                className="flex-1 cursor-pointer"
                style={{ accentColor: '#10b981', color: '#10b981', ['--p' as any]: `${Math.max(0, Math.min(100, (p.hypochloriteOutput_kgh / 500) * 100)).toFixed(2)}%` }} />
              <input type="number" min={0.1} max={500} step={0.1} value={p.hypochloriteOutput_kgh}
                onChange={e => setP(prev => ({ ...prev, hypochloriteOutput_kgh: Math.max(0.1, Math.min(500, Number(e.target.value) || 0)) }))}
                className="w-24 bg-rb-elevated border border-emerald-500/30 rounded-lg px-2 py-1.5 text-base font-bold text-emerald-300 tabular-nums text-right" />
              <span className="text-xs text-slate-500">kg/h</span>
            </div>
          </div>
          <div className="md:col-span-4 flex justify-end gap-2">
            <button onClick={() => setP(prev => ({ ...prev, ...recommendAll(prev) }))}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/10 text-emerald-300 text-xs font-bold border border-emerald-500/40 hover:bg-emerald-500/20">
              ⚙ Re-size everything from this Cl₂
            </button>
          </div>
        </div>
        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
          <div className="p-2 rb-inner-card">
            <span className="text-slate-500">Seawater flow </span>
            <span className="text-cyan-300 font-bold tabular-nums">{p.seawaterFlow_PDoff_m3h.toFixed(2)} m³/h</span>
          </div>
          <div className="p-2 rb-inner-card">
            <span className="text-slate-500">Modules </span>
            <span className="text-cyan-300 font-bold tabular-nums">{p.modules_installed} duty + {p.modules_standby} standby</span>
          </div>
          <div className="p-2 rb-inner-card">
            <span className="text-slate-500">Power AC </span>
            <span className="text-amber-300 font-bold tabular-nums">{r.power_total_AC_kW.toFixed(0)} kW</span>
          </div>
          <div className="p-2 rb-inner-card">
            <span className="text-slate-500">Spec. energy </span>
            <span className={`font-bold tabular-nums ${r.specificEnergy_kWh_per_kgCl2 < 5.5 ? 'text-emerald-300' : r.specificEnergy_kWh_per_kgCl2 < 7 ? 'text-amber-300' : 'text-rose-300'}`}>
              {r.specificEnergy_kWh_per_kgCl2.toFixed(2)} kWh/kg Cl₂
            </span>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2">
          <input type="text" value={configName} onChange={e => setConfigName(e.target.value)} placeholder="Configuration name"
            className="flex-1 bg-rb-elevated border border-slate-800 rounded px-2 py-1 text-xs text-slate-200" />
          {cloudConfigId ? <span className="text-[10px] text-purple-400">cloud id #{cloudConfigId}</span> : <span className="text-[10px] text-amber-400">local only</span>}
        </div>
      </div>

      {/* CLOUD PICKER POPUP */}
      {cloudPickerOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
             onClick={() => setCloudPickerOpen(false)}>
          <div className="rb-card w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col"
               onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-slate-800">
              <h3 className="text-sm font-bold text-white">☁ Saved EC configurations ({cloudList.length})</h3>
              <button onClick={() => setCloudPickerOpen(false)} className="p-1 text-slate-500 hover:text-white">✕</button>
            </div>
            <div className="overflow-y-auto p-3 space-y-2">
              {cloudLoading && <p className="text-xs text-slate-500 text-center py-6">Loading…</p>}
              {!cloudLoading && cloudList.length === 0 && <p className="text-xs text-slate-500 text-center py-6">No saved configs yet.</p>}
              {cloudList.map(cfg => (
                <div key={cfg.id} className="p-3 rb-inner-card flex items-center justify-between hover:bg-slate-800/30">
                  <button onClick={() => handleCloudLoad(cfg.id)} className="flex-1 text-left">
                    <p className="text-xs font-bold text-white">{cfg.name}</p>
                    <p className="text-[10px] text-slate-500">
                      id #{cfg.id} · {Number(cfg.cl2_kgh).toFixed(1)} kg/h Cl₂ · {cfg.modules} modules · {new Date(cfg.updated_at).toLocaleString()}
                    </p>
                  </button>
                  <button onClick={() => handleCloudDelete(cfg.id, cfg.name)}
                    className="p-1.5 rounded text-rose-400 hover:bg-rose-500/20">🗑</button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* TOP TABS */}
      <div className="flex flex-wrap items-center gap-2 border-b border-slate-700 pb-2">
        {([
          { id: 'process',   label: 'Process & sizing' },
          { id: 'chemistry', label: 'Detailed seawater chemistry' },
          { id: 'config',    label: 'System configuration' },
        ] as { id: TopTab; label: string }[]).map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors ${
              tab === t.id
                ? 'bg-cyan-600/20 border-cyan-500 text-cyan-200'
                : 'bg-slate-900/40 border-slate-700 text-slate-300 hover:border-slate-500'
            }`}>{t.label}</button>
        ))}
        <button onClick={() => setP(prev => ({ ...prev, ...recommendAll(prev) }))}
          className="ml-auto px-3 py-1.5 rounded-md text-xs font-semibold bg-emerald-600/20 border border-emerald-500/60 text-emerald-200 hover:bg-emerald-600/30 inline-flex items-center gap-1.5">
          <Activity className="w-3.5 h-3.5" /> Apply all recommendations
        </button>
        <span className="text-[11px] text-slate-400">
          Preset:{' '}
          <span className="text-cyan-300 font-semibold">
            {preset === 'oneOne' ? '1 duty + 1 standby'
              : preset === 'twoOne' ? '2 duty + 1 standby'
              : preset === 'threeOne' ? '3 duty + 1 standby'
              : 'Custom'}
          </span>
        </span>
      </div>

      {tab === 'config' && (
        <Section title="System configuration - select preset" icon={<Settings className="w-4 h-4" />}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {([
              { id: 'oneOne',   title: '1 duty + 1 standby', desc: 'Single duty module, full N+1 redundancy across electrolyser, dosing, blower and acid pumps. Smallest footprint, typical for offshore platforms and small intakes.' },
              { id: 'twoOne',   title: '2 duty + 1 standby', desc: 'Split duty across two modules with one common standby; matched 2-duty dosing pumps and blowers. Mid-size plants (1 000 - 3 000 m3/h).' },
              { id: 'threeOne', title: '3 duty + 1 standby', desc: 'Large intake configuration; three duty modules with one common standby. Used for >= 3 000 m3/h coastal plants.' },
              { id: 'custom',   title: 'Custom',             desc: 'No preset applied - configure every duty / standby count manually in the Process tab.' },
            ] as { id: SystemPreset; title: string; desc: string }[]).map(opt => (
              <button key={opt.id} onClick={() => choosePreset(opt.id)}
                className={`text-left p-3 rounded-lg border transition-colors ${
                  preset === opt.id
                    ? 'bg-cyan-600/15 border-cyan-500'
                    : 'bg-slate-900/40 border-slate-700 hover:border-slate-500'
                }`}>
                <div className="text-sm font-semibold text-cyan-200 mb-1">{opt.title}</div>
                <div className="text-[11px] text-slate-400 leading-snug">{opt.desc}</div>
              </button>
            ))}
          </div>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-[11px]">
            <div className="rounded-md bg-slate-900/40 border border-slate-700 px-3 py-2">
              <div className="text-slate-400">Electrolyser modules</div>
              <div className="text-slate-100 font-semibold">{p.modules_installed} duty + {p.modules_standby} standby</div>
            </div>
            <div className="rounded-md bg-slate-900/40 border border-slate-700 px-3 py-2">
              <div className="text-slate-400">NaOCl dosing pumps</div>
              <div className="text-slate-100 font-semibold">{p.dosingPumps_duty} duty + {p.dosingPumps_standby} standby</div>
            </div>
            <div className="rounded-md bg-slate-900/40 border border-slate-700 px-3 py-2">
              <div className="text-slate-400">H2 dilution blowers</div>
              <div className="text-slate-100 font-semibold">{p.blower_duty} duty + {p.blower_standby} standby</div>
            </div>
            <div className="rounded-md bg-slate-900/40 border border-slate-700 px-3 py-2">
              <div className="text-slate-400">Acid wash pumps</div>
              <div className="text-slate-100 font-semibold">{p.acidPumps_duty} duty + {p.acidPumps_standby} standby</div>
            </div>
          </div>
          <p className="mt-3 text-[11px] text-slate-400 italic">
            Selecting a preset overwrites the duty / standby counts across all four equipment groups in one click. After applying
            a preset you may still tune individual counts in the Process tab - the preset will switch to "Custom" if you do.
          </p>
        </Section>
      )}

      {tab === 'chemistry' && (
        <Section title="Detailed seawater chemistry (used in process calculations)" icon={<Beaker className="w-4 h-4" />}>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            <NumField label="TDS"                  suffix="mg/L"             value={p.TDS_mgL}                min={0}    step={100}  onChange={v => setP({ ...p, TDS_mgL: v })} help="Reference 35 000 at S=35 ppt" />
            <NumField label="Chloride (Cl-)"       suffix="mg/L"             value={p.chloride_mgL}           min={0}    step={100}  onChange={v => setP({ ...p, chloride_mgL: v })} help="Drives Cl2 yield; ref 19 000" />
            <NumField label="Bromide (Br-)"        suffix="mg/L"             value={p.bromide_mgL}            min={0}    step={1}    onChange={v => setP({ ...p, bromide_mgL: v })} help="Bromate formation precursor" />
            <NumField label="Sulphate (SO4 2-)"    suffix="mg/L"             value={p.sulphate_mgL}           min={0}    step={50}   onChange={v => setP({ ...p, sulphate_mgL: v })} />
            <NumField label="Calcium hardness"     suffix="mg/L as CaCO3"    value={p.calciumHardness_mgL}    min={0}    step={10}   onChange={v => setP({ ...p, calciumHardness_mgL: v })} help="Cathodic CaCO3 scale" />
            <NumField label="Magnesium hardness"   suffix="mg/L as CaCO3"    value={p.magnesiumHardness_mgL}  min={0}    step={50}   onChange={v => setP({ ...p, magnesiumHardness_mgL: v })} help="Cathodic Mg(OH)2 scale" />
            <NumField label="Total alkalinity"     suffix="mg/L as CaCO3"    value={p.totalAlkalinity_mgL}    min={0}    step={5}    onChange={v => setP({ ...p, totalAlkalinity_mgL: v })} help="Neutralisation buffer" />
            <NumField label="Free CO2"             suffix="mg/L"             value={p.freeCO2_mgL}            min={0}    step={0.1}  onChange={v => setP({ ...p, freeCO2_mgL: v })} />
            <NumField label="Silica (SiO2)"        suffix="mg/L"             value={p.silica_mgL}             min={0}    step={0.5}  onChange={v => setP({ ...p, silica_mgL: v })} />
            <NumField label="Iron + Manganese"     suffix="mg/L"             value={p.ironMn_mgL}             min={0}    step={0.01} onChange={v => setP({ ...p, ironMn_mgL: v })} help="Electrode-fouling risk if > 0.3" />
            <NumField label="Total organic carbon" suffix="mg/L"             value={p.organic_TOC_mgL}        min={0}    step={0.1}  onChange={v => setP({ ...p, organic_TOC_mgL: v })} help="THM / HAA precursor pool" />
            <NumField label="pH (feed)"                                       value={p.pH_feed}                min={5}    max={10}    step={0.1}  onChange={v => setP({ ...p, pH_feed: v })} />
          </div>

          <h4 className="mt-4 mb-2 text-xs font-semibold text-cyan-300">Live effects on process calculations</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KPI label="Cl- correction factor"       value={r.Cl_factor.toFixed(3)}                  unit="x nominal CE" tone={r.Cl_factor >= 0.9 ? 'good' : 'warn'} />
            <KPI label="Adjusted current eff."       value={r.adjusted_CE_pc.toFixed(1)}             unit="%"            tone={r.adjusted_CE_pc >= 85 ? 'good' : 'warn'} />
            <KPI label="Total hardness"              value={r.totalHardness_mgL.toFixed(0)}          unit="mg/L as CaCO3" />
            <KPI label="Recommended wash interval"   value={r.recommendedWashInterval_d.toFixed(0)}  unit="days" tone={p.acidWashEvery_d <= r.recommendedWashInterval_d * 1.1 ? 'good' : 'warn'} />
            <KPI label="Bromate formation risk"      value={r.bromate_risk}                          unit=""             tone={r.bromate_risk === 'High' || r.bromate_risk === 'Moderate' ? 'warn' : 'good'} />
            <KPI label="Neutralisation alkalinity"   value={r.neutralisation_NaOH_kg_per_event_basis.toFixed(0)} unit="mg/L (basis)" />
            <KPI label="Iron + Mn"                   value={p.ironMn_mgL.toFixed(2)}                 unit="mg/L"         tone={p.ironMn_mgL < 0.3 ? 'good' : 'warn'} />
            <KPI label="TOC"                         value={p.organic_TOC_mgL.toFixed(2)}            unit="mg/L"         tone={p.organic_TOC_mgL < 5 ? 'good' : 'warn'} />
          </div>
          <p className="mt-3 text-[11px] text-slate-400 italic">
            Chloride drives the effective current efficiency, the cell-stack current and the AC power - therefore the entire
            sizing chain re-balances live when this value is changed. Hardness drives the recommended acid-wash interval
            (CaCO3 + Mg(OH)2 scaling rate). Bromide flags the bromate formation pathway downstream. Iron + Mn is the
            electrode-fouling indicator (target {'<'} 0.3 mg/L).
          </p>
        </Section>
      )}

      {tab !== 'process' ? null : (
      <>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        <KPI label="Seawater PD-off" value={p.seawaterFlow_PDoff_m3h.toFixed(1)}   unit="m3/h" />
        <KPI label="Electrolyser in" value={p.electrolyserInlet_m3h.toFixed(1)}    unit="m3/h" />
        <KPI label="NaOCl out (Cl2)" value={p.hypochloriteOutput_kgh.toFixed(1)}   unit="kg/h" tone="good" />
        <KPI label="Free Cl2 dose"   value={r.freeCl2_dose_mgL.toFixed(1)}         unit="mg/L"
          tone={r.freeCl2_dose_mgL >= 0.5 && r.freeCl2_dose_mgL <= 10 ? 'good' : 'warn'} />
        <KPI label="AC power (stack)" value={r.power_total_AC_kW.toFixed(2)} unit="kW" />
        <KPI label="Dosing pumps"     value={(r.dosingPump_kW_each * p.dosingPumps_duty).toFixed(2)} unit="kW total" />
        <KPI label="Blowers"          value={(r.blower_motor_kW_each * p.blower_duty).toFixed(2)} unit="kW total" />
        <KPI label="H2 vent"          value={r.H2_Nm3h.toFixed(2)} unit="Nm3/h" tone={r.vent_safe ? 'good' : 'bad'} />
      </div>

      {/* PROCESS FLOW DIAGRAM */}
      <Section title="Process flow diagram - live values" icon={<Activity className="w-4 h-4" />}>
        <ProcessDiagram p={p} r={r} />
        <p className="mt-2 text-[10px] text-slate-500 italic">
          Streams and equipment update live with the inputs on the left. Green tones = passing safety / engineering envelope;
          amber = warning; red = critical.
        </p>
      </Section>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* INPUTS COLUMN */}
        <div className="space-y-5">
          <Section title="1. Plant flow architecture + seawater feed" icon={<Droplets className="w-4 h-4" />}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <NumField label="Seawater flow - PD OFF (max)"  suffix="m3/h" value={p.seawaterFlow_PDoff_m3h}  min={1}  max={500}  step={0.5} onChange={v => setP({ ...p, seawaterFlow_PDoff_m3h: v })} help="Max treated-water flow when pressure-drop restriction is off" />
              <NumField label="Electrolyser inlet flow"       suffix="m3/h" value={p.electrolyserInlet_m3h}   min={0.5} max={300}  step={0.5} onChange={v => setP({ ...p, electrolyserInlet_m3h: v })} help="Brine feed to electrolyser cells (split off the seawater intake)" />
              <NumField label="Hypochlorite output (Cl2)"     suffix="kg/h" value={p.hypochloriteOutput_kgh}  min={0.1} max={500}  step={0.1} onChange={v => setP({ ...p, hypochloriteOutput_kgh: v })} help="Product Cl2 mass produced - drives the cell + dosing sizing" />
              <NumField label="Salinity"                      suffix="ppt"   value={p.salinity_ppt}           min={0}   max={45}   step={0.5} onChange={v => setP({ ...p, salinity_ppt: v })} help="Open ocean ~ 35; brackish 5 - 25" />
              <NumField label="Temperature"                   suffix="deg C" value={p.temperature_C}          min={-2}  max={45}   step={0.5} onChange={v => setP({ ...p, temperature_C: v })} help="Sets conductivity and Cl2 solubility" />
              <NumField label="pH (feed)"                                    value={p.pH_feed}                min={5}   max={10}   step={0.1} onChange={v => setP({ ...p, pH_feed: v })} />
              <NumField label="TSS"                           suffix="mg/L"  value={p.TSS_mgL}                min={0}   max={500}  step={1}   onChange={v => setP({ ...p, TSS_mgL: v })} />
              <NumField label="Turbidity"                     suffix="NTU"   value={p.turbidity_NTU}          min={0}   max={50}   step={0.5} onChange={v => setP({ ...p, turbidity_NTU: v })} />
              <SelField label="Duty mode" value={p.dutyMode} options={[
                { value: 'continuous', label: 'Continuous (24/7)' },
                { value: 'shock',      label: 'Intermittent / shock (set hours/day)' },
                { value: 'cyclic',     label: 'Cyclic ON / OFF (e.g. 30 / 30, 20 / 20)' },
              ]} onChange={v => setP({ ...p, dutyMode: v })} />
              {p.dutyMode === 'shock' && (
                <NumField label="Shock hours / day" suffix="h" value={p.shockHoursPerDay} min={0.5} max={24} step={0.5} onChange={v => setP({ ...p, shockHoursPerDay: v })} />
              )}
              {p.dutyMode === 'cyclic' && (
                <>
                  <NumField label="Cycle ON time"  suffix="min" value={p.cycleOnMin}  min={1} max={240} step={1} onChange={v => setP({ ...p, cycleOnMin: v })}  help="Rectifier energised; Cl₂ produced" />
                  <NumField label="Cycle OFF time" suffix="min" value={p.cycleOffMin} min={1} max={240} step={1} onChange={v => setP({ ...p, cycleOffMin: v })} help="Rectifier idle; storage tank covers dosing demand" />
                </>
              )}
            </div>

            {/* Cyclic-mode derived metrics + presets */}
            {p.dutyMode === 'cyclic' && (() => {
              const cyclesPerDay = 1440 / cycleTotalMin;
              const dailyCl2_kg = p.hypochloriteOutput_kgh * dailyDuty_h;
              const dailyCl2_continuous_kg = p.hypochloriteOutput_kgh * 24;
              const minStorageH = p.cycleOffMin / 60; // storage tank must cover one OFF segment
              const storageOK = (p.storageTank_h ?? 2) >= minStorageH;
              return (
                <div className="mt-3 space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-[11px] text-slate-400 mr-1">Presets:</span>
                    {[
                      { on: 30, off: 30, label: '30/30' },
                      { on: 20, off: 20, label: '20/20' },
                      { on: 15, off: 45, label: '15/45' },
                      { on: 60, off: 60, label: '60/60' },
                    ].map(pr => (
                      <button key={pr.label} onClick={() => setP({ ...p, cycleOnMin: pr.on, cycleOffMin: pr.off })}
                        className={`px-2 py-1 rounded-md text-[10px] font-bold border ${
                          p.cycleOnMin === pr.on && p.cycleOffMin === pr.off
                            ? 'bg-cyan-500/20 border-cyan-400 text-cyan-200'
                            : 'bg-slate-800/40 border-slate-700 text-slate-300 hover:border-cyan-500/40 hover:text-cyan-300'
                        }`}>
                        {pr.label} min
                      </button>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
                    <div className="rounded-md bg-emerald-900/20 border border-emerald-700 px-2 py-1.5">
                      <span className="text-slate-400">ON hours/day </span>
                      <span className="text-emerald-200 font-semibold">{dailyDuty_h.toFixed(1)} h</span>
                      <div className="text-[10px] text-slate-500">{(cycleDutyFrac * 100).toFixed(0)}% duty</div>
                    </div>
                    <div className="rounded-md bg-cyan-900/20 border border-cyan-700 px-2 py-1.5">
                      <span className="text-slate-400">Cycles/day </span>
                      <span className="text-cyan-200 font-semibold">{cyclesPerDay.toFixed(0)}</span>
                      <div className="text-[10px] text-slate-500">@ {cycleTotalMin.toFixed(0)} min each</div>
                    </div>
                    <div className="rounded-md bg-amber-900/20 border border-amber-700 px-2 py-1.5">
                      <span className="text-slate-400">Cl₂/day (this duty) </span>
                      <span className="text-amber-200 font-semibold">{dailyCl2_kg.toFixed(1)} kg</span>
                      <div className="text-[10px] text-slate-500">vs continuous {dailyCl2_continuous_kg.toFixed(0)} kg</div>
                    </div>
                    <div className={`rounded-md border px-2 py-1.5 ${storageOK ? 'bg-emerald-900/20 border-emerald-700' : 'bg-rose-900/20 border-rose-700'}`}>
                      <span className="text-slate-400">Storage need </span>
                      <span className={`font-semibold ${storageOK ? 'text-emerald-200' : 'text-rose-200'}`}>{minStorageH.toFixed(2)} h</span>
                      <div className="text-[10px] text-slate-500">{storageOK ? '✓ tank covers OFF' : `✗ raise to ≥ ${minStorageH.toFixed(2)} h`}</div>
                    </div>
                  </div>
                  <p className="text-[10px] text-slate-500 italic leading-relaxed">
                    Cyclic mode: rectifier energises for {p.cycleOnMin} min, idles for {p.cycleOffMin} min, then repeats — {cyclesPerDay.toFixed(0)} cycles per 24 h.
                    Average daily Cl₂ scales by the duty fraction ({(cycleDutyFrac * 100).toFixed(0)} % of the instantaneous rate).
                    The hypochlorite storage tank must hold at least <strong className="text-slate-300">{minStorageH.toFixed(2)} h</strong> of dosing demand to cover one OFF segment without interrupting downstream injection.
                    The blower runs through ON segments plus pre-/post-purge ({p.blower_purge_min} min each side, every cycle).
                  </p>
                </div>
              );
            })()}
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
              <div className="rounded-md bg-cyan-900/20 border border-cyan-700 px-2 py-1.5">
                <span className="text-slate-400">Free Cl2 dose (derived)</span>{' '}
                <span className="text-cyan-200 font-semibold">{r.freeCl2_dose_mgL.toFixed(2)} mg/L</span>
              </div>
              <div className="rounded-md bg-cyan-900/20 border border-cyan-700 px-2 py-1.5">
                <span className="text-slate-400">Brine / module (derived)</span>{' '}
                <span className="text-cyan-200 font-semibold">{r.brineFlow_per_module_m3h.toFixed(2)} m3/h</span>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Density</span>{' '}
                <span className="text-slate-100 font-medium">{r.rho.toFixed(1)} kg/m3</span>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Conductivity</span>{' '}
                <span className="text-slate-100 font-medium">{r.cond.toFixed(1)} mS/cm</span>
              </div>
            </div>
            <p className="mt-2 text-[11px] text-slate-400 italic">
              The three plant flows are interdependent: the hypochlorite output divided by the PD-off seawater flow defines the
              free Cl2 dose; the electrolyser inlet divided by the module count defines the per-module brine flow used in the
              cell-stack and pipe-velocity calculations.
            </p>
          </Section>

          <Section title="1b. Seawater pre-filter (sized from inflow)" icon={<Droplets className="w-4 h-4" />}>
            <div className="grid grid-cols-2 gap-3">
              <NumField label="Micron rating"        suffix="um"          value={p.filter_micron_um}        min={20}  max={500}  step={5}  onChange={v => setP({ ...p, filter_micron_um: v })} help="Electrochlorination pre-filter typ. 80 - 100 um" />
              <NumField label="Design DP trigger"    suffix="bar"         value={p.filter_designDP_bar}     min={0.1} max={1.5}  step={0.05} onChange={v => setP({ ...p, filter_designDP_bar: v })} help="Backwash starts at this clean-to-dirty DP" />
              <NumField label="Filtration rate"      suffix="m3/m2/h"     value={p.filter_rate_m3_m2_h}     min={20}  max={250}  step={5}  onChange={v => setP({ ...p, filter_rate_m3_m2_h: v })} help="Self-cleaning strainer 80 - 150 envelope" />
              <SelField label="Inlet (standard)"                                value={String(p.filter_inlet_DN) as string}
                options={STD_PIPES.map(s => ({ value: String(s.dn), label: `${s.label} (ID ${s.id_mm.toFixed(1)} mm)` }))}
                onChange={v => setP({ ...p, filter_inlet_DN: parseInt(v, 10) || 65 })} />
              <SelField label="Outlet (standard)"                               value={String(p.filter_outlet_DN) as string}
                options={STD_PIPES.map(s => ({ value: String(s.dn), label: `${s.label} (ID ${s.id_mm.toFixed(1)} mm)` }))}
                onChange={v => setP({ ...p, filter_outlet_DN: parseInt(v, 10) || 65 })} />
            </div>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
              <div className="rounded-md bg-cyan-900/20 border border-cyan-700 px-2 py-1.5">
                <span className="text-slate-400">Filter flow (= PD-off)</span>{' '}
                <span className="text-cyan-200 font-semibold">{r.filter_flow_m3h.toFixed(2)} m3/h</span>
              </div>
              <div className="rounded-md bg-cyan-900/20 border border-cyan-700 px-2 py-1.5">
                <span className="text-slate-400">Filter area (derived)</span>{' '}
                <span className="text-cyan-200 font-semibold">{r.filter_area_m2.toFixed(3)} m2</span>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Units</span>{' '}
                <span className="text-slate-100 font-semibold">{r.filter_units_duty} duty + {r.filter_units_standby} standby</span>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Backwash flow</span>{' '}
                <span className="text-slate-100 font-semibold">{r.filter_backwash_m3h.toFixed(2)} m3/h</span>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Inlet v</span>{' '}
                <span className={`font-semibold ${r.filter_in_vel >= 1 && r.filter_in_vel <= 3 ? 'text-emerald-300' : 'text-amber-300'}`}>{r.filter_in_vel.toFixed(2)} m/s</span>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Outlet v</span>{' '}
                <span className={`font-semibold ${r.filter_out_vel >= 1 && r.filter_out_vel <= 3 ? 'text-emerald-300' : 'text-amber-300'}`}>{r.filter_out_vel.toFixed(2)} m/s</span>
              </div>
              <div className="rounded-md bg-cyan-900/20 border border-cyan-700 px-2 py-1.5">
                <span className="text-slate-400">Rec. inlet DN</span>{' '}
                <span className="text-cyan-200 font-semibold">DN{r.recommended_filter_in_DN}</span>
              </div>
              <div className="rounded-md bg-cyan-900/20 border border-cyan-700 px-2 py-1.5">
                <span className="text-slate-400">Rec. outlet DN</span>{' '}
                <span className="text-cyan-200 font-semibold">DN{r.recommended_filter_out_DN}</span>
              </div>
            </div>
            <p className="mt-2 text-[11px] text-slate-400 italic">
              The filter is sized to the PD-off seawater flow (max plant flow). Filter area = inflow / filtration rate; inlet
              and outlet pipe sizes follow the same 2 m/s seawater design velocity used elsewhere. 1 duty + 1 standby matches
              the system redundancy preset; backwash flow envelope is set at 4 % of forward flow.
            </p>
          </Section>

          <Section title="2. Electrolysis cell stack" icon={<Layers className="w-4 h-4" />}
            action={<RecommendBtn onClick={() => setP(prev => ({ ...prev, ...recommendStack(prev) }))} />}>
            <div className="grid grid-cols-2 gap-3">
              <SelField label="Cell topology" value={p.cellTech} options={[
                { value: 'bipolar',   label: 'Bipolar (cells in series)' },
                { value: 'monopolar', label: 'Monopolar (parallel)' },
              ]} onChange={v => setP({ ...p, cellTech: v })} />
              <NumField label="Current density"   suffix="kA/m^2" value={p.currentDensity_kAm2}    min={1.0} max={6.0} step={0.1} onChange={v => setP({ ...p, currentDensity_kAm2: v })} help="2 - 4 kA/m^2 envelope" />
              <NumField label="Cell voltage"      suffix="V"      value={p.cellVoltage_V}          min={2.5} max={5.5} step={0.05} onChange={v => setP({ ...p, cellVoltage_V: v })} help="MMO Pt/Ir typ. 3.5 - 4.5 V" />
              <NumField label="Current efficiency" suffix="%"     value={p.currentEfficiency_pc}   min={70}  max={100} step={1}  onChange={v => setP({ ...p, currentEfficiency_pc: v })} help="Single-pass seawater 85 - 95 %" />
              <NumField label="Cells / module"                    value={p.cells_per_module}       min={1}   max={200} step={1}  onChange={v => setP({ ...p, cells_per_module: v })} />
              <NumField label="Modules (duty)"                    value={p.modules_installed}      min={1}   max={20}  step={1}  onChange={v => setP({ ...p, modules_installed: v })} />
              <NumField label="Modules (standby)"                 value={p.modules_standby}        min={0}   max={10}  step={1}  onChange={v => setP({ ...p, modules_standby: v })} />
              <NumField label="Brine velocity"      suffix="m/s"  value={p.brineVelocity_ms}       min={0.1} max={1.5} step={0.05} onChange={v => setP({ ...p, brineVelocity_ms: v })} />
              <div className="rounded-md bg-cyan-900/15 border border-cyan-800/60 px-2 py-2 text-[11px] flex flex-col justify-center">
                <span className="text-slate-400">Brine flow / module (derived)</span>
                <span className="text-cyan-200 font-semibold">{r.brineFlow_per_module_m3h.toFixed(2)} m3/h</span>
                <span className="text-[9.5px] text-slate-500">= electrolyser inlet / modules</span>
              </div>
            </div>
          </Section>

          <Section title="2b. Cell operating conditions (nameplate)" icon={<Settings className="w-4 h-4" />}
            action={<RecommendBtn onClick={() => setP(prev => ({ ...prev, ...recommendStack(prev) }))} />}>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <NumField label="Rated DC current (nameplate)" suffix="A" value={p.cell_ratedDC_current_A} min={50} max={20000} step={50} onChange={v => setP({ ...p, cell_ratedDC_current_A: v })} help="Calculated by default from Faraday + demand; editable." />
                <div className="mt-1 flex items-center justify-between text-[10px]">
                  <span className="text-slate-500">Calculated (Faraday + 15 % margin):{' '}
                    <span className="text-cyan-300 font-semibold">{r.rated_current_calc_A.toFixed(0)} A</span>
                  </span>
                  <button onClick={() => setP({ ...p, cell_ratedDC_current_A: r.rated_current_calc_A })}
                    className="px-1.5 py-0.5 rounded bg-cyan-700/30 hover:bg-cyan-700/50 border border-cyan-700 text-cyan-200 text-[10px]">
                    use calc
                  </button>
                </div>
              </div>
              <NumField label="Rated DC voltage" suffix="V" value={p.cell_ratedDC_voltage_V} min={20} max={500} step={1} onChange={v => setP({ ...p, cell_ratedDC_voltage_V: v })} help="Module nameplate stack voltage (default 61 V)" />
              <div>
                <NumField label="Rated capacity" suffix="kg Cl2/h" value={p.cell_ratedCapacity_kgCl2h} min={0.1} max={200} step={0.1} onChange={v => setP({ ...p, cell_ratedCapacity_kgCl2h: v })} help="Per-module nameplate Cl2 yield" />
                <div className="mt-1 flex items-center justify-between text-[10px]">
                  <span className="text-slate-500">Calculated at I_rated:{' '}
                    <span className="text-cyan-300 font-semibold">{r.rated_capacity_calc_kgCl2h.toFixed(2)} kg/h</span>
                  </span>
                  <button onClick={() => setP({ ...p, cell_ratedCapacity_kgCl2h: parseFloat(r.rated_capacity_calc_kgCl2h.toFixed(2)) })}
                    className="px-1.5 py-0.5 rounded bg-cyan-700/30 hover:bg-cyan-700/50 border border-cyan-700 text-cyan-200 text-[10px]">
                    use calc
                  </button>
                </div>
              </div>
              <NumField label="Operating pressure"   suffix="barg"        value={p.cell_operatingPressure_barg} min={0.1} max={6}    step={0.1} onChange={v => setP({ ...p, cell_operatingPressure_barg: v })} help="0.5 - 3 barg envelope" />
              <SelField label="Inlet connection (standard)"               value={String(p.cell_inlet_DN) as string}
                options={STD_PIPES.map(s => ({ value: String(s.dn), label: `${s.label}  (ID ${s.id_mm.toFixed(1)} mm)` }))}
                onChange={(v) => setP({ ...p, cell_inlet_DN: parseInt(v, 10) || 80 })} />
              <SelField label="Outlet connection (standard)"              value={String(p.cell_outlet_DN) as string}
                options={STD_PIPES.map(s => ({ value: String(s.dn), label: `${s.label}  (ID ${s.id_mm.toFixed(1)} mm)` }))}
                onChange={(v) => setP({ ...p, cell_outlet_DN: parseInt(v, 10) || 80 })} />
            </div>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Inlet v</span>{' '}
                <span className={`font-semibold ${r.inlet_vel_ms >= 0.4 && r.inlet_vel_ms <= 1.5 ? 'text-emerald-300' : 'text-amber-300'}`}>{r.inlet_vel_ms.toFixed(2)} m/s</span>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Outlet v</span>{' '}
                <span className={`font-semibold ${r.outlet_vel_ms >= 0.3 && r.outlet_vel_ms <= 1.2 ? 'text-emerald-300' : 'text-amber-300'}`}>{r.outlet_vel_ms.toFixed(2)} m/s</span>
              </div>
              <div className="rounded-md bg-cyan-900/20 border border-cyan-700 px-2 py-1.5">
                <span className="text-slate-400">Recommended inlet</span>{' '}
                <span className="text-cyan-200 font-semibold">DN{r.recommended_inlet_DN}</span>
              </div>
              <div className="rounded-md bg-cyan-900/20 border border-cyan-700 px-2 py-1.5">
                <span className="text-slate-400">Recommended outlet</span>{' '}
                <span className="text-cyan-200 font-semibold">DN{r.recommended_outlet_DN}</span>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Current util.</span>{' '}
                <span className={`font-semibold ${r.cell_current_util_pc >= 60 && r.cell_current_util_pc <= 105 ? 'text-emerald-300' : 'text-amber-300'}`}>{r.cell_current_util_pc.toFixed(0)} % of rated</span>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Voltage util.</span>{' '}
                <span className={`font-semibold ${r.cell_voltage_util_pc >= 70 && r.cell_voltage_util_pc <= 105 ? 'text-emerald-300' : 'text-amber-300'}`}>{r.cell_voltage_util_pc.toFixed(0)} % of rated</span>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Capacity util.</span>{' '}
                <span className={`font-semibold ${r.cell_capacity_util_pc >= 50 && r.cell_capacity_util_pc <= 105 ? 'text-emerald-300' : 'text-amber-300'}`}>{r.cell_capacity_util_pc.toFixed(0)} % of rated</span>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">P_op band</span>{' '}
                <span className={`font-semibold ${r.pressure_in_band ? 'text-emerald-300' : 'text-rose-300'}`}>{p.cell_operatingPressure_barg.toFixed(2)} barg</span>
              </div>
            </div>
            <p className="mt-3 text-[11px] text-slate-400 italic">
              Inlet / outlet selections are restricted to standard DN sizes used by electrochlorination cell manufacturers
              (De Nora, Severn Trent, Evoqua, Hidrostal); inside diameters follow BS EN ISO 15493 Sch 80 PVDF (BS EN 1092-1
              / ASME B16.5 flange faces). Velocities are computed from the per-module brine flow on the actual pipe ID.
            </p>
          </Section>

          <Section title="3. Hypochlorite dosing pumps" icon={<Gauge className="w-4 h-4" />}
            action={<RecommendBtn onClick={() => setP(prev => ({ ...prev, ...recommendDosing(prev) }))} />}>
            <div className="grid grid-cols-2 gap-3">
              <NumField label="Dosing pumps (duty)"                  value={p.dosingPumps_duty}        min={1}  max={6} step={1}  onChange={v => setP({ ...p, dosingPumps_duty: v })} />
              <NumField label="Dosing pumps (standby)"               value={p.dosingPumps_standby}     min={0}  max={6} step={1}  onChange={v => setP({ ...p, dosingPumps_standby: v })} />
              <NumField label="Pump capacity each"  suffix="L/h"     value={p.dosingPump_capacity_Lh_each} min={0} max={5000} step={1} onChange={v => setP({ ...p, dosingPump_capacity_Lh_each: v })} help="0 = auto-derive from demand" />
              <NumField label="Pump discharge head" suffix="bar"     value={p.dosingPumpHead_bar}     min={0.5} max={20} step={0.1} onChange={v => setP({ ...p, dosingPumpHead_bar: v })} help="To overcome injection-quill back-pressure" />
              <NumField label="System static pressure" suffix="mbar" value={p.dosingPump_staticPressure_mbar} min={0} max={5000} step={10} onChange={v => setP({ ...p, dosingPump_staticPressure_mbar: v })} help="Static elevation + line losses" />
              <NumField label="Storage residence"   suffix="h"       value={p.storageTank_h}          min={0.5} max={8}  step={0.5} onChange={v => setP({ ...p, storageTank_h: v })} help="NaOCl decay limits to <= 4 h" />
              <SelField label="Inlet (standard)"                     value={String(p.dosingPump_inlet_DN) as string}
                options={STD_PIPES.map(s => ({ value: String(s.dn), label: `${s.label} (ID ${s.id_mm.toFixed(1)} mm)` }))}
                onChange={v => setP({ ...p, dosingPump_inlet_DN: parseInt(v, 10) || 25 })} />
              <SelField label="Outlet (standard)"                    value={String(p.dosingPump_outlet_DN) as string}
                options={STD_PIPES.map(s => ({ value: String(s.dn), label: `${s.label} (ID ${s.id_mm.toFixed(1)} mm)` }))}
                onChange={v => setP({ ...p, dosingPump_outlet_DN: parseInt(v, 10) || 25 })} />
            </div>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
              <div className="rounded-md bg-cyan-900/20 border border-cyan-700 px-2 py-1.5">
                <span className="text-slate-400">Demand</span>{' '}
                <span className="text-cyan-200 font-semibold">{r.dosingPumpFlow_total_demand_Lh.toFixed(0)} L/h</span>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Installed</span>{' '}
                <span className="text-slate-100 font-semibold">{r.dosingPumpFlow_total_Lh.toFixed(0)} L/h</span>
              </div>
              <div className={`rounded-md border px-2 py-1.5 ${r.dosing_margin_pc >= 0 ? 'bg-emerald-900/20 border-emerald-700' : 'bg-rose-900/20 border-rose-700'}`}>
                <span className="text-slate-400">Margin</span>{' '}
                <span className="text-slate-100 font-semibold">{r.dosing_margin_pc > 0 ? '+' : ''}{r.dosing_margin_pc.toFixed(1)} %</span>
              </div>
              <div className="rounded-md bg-cyan-900/20 border border-cyan-700 px-2 py-1.5">
                <span className="text-slate-400">Rec. inlet / outlet</span>{' '}
                <span className="text-cyan-200 font-semibold">DN{r.recommended_dosing_in_DN} / DN{r.recommended_dosing_out_DN}</span>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Inlet v</span>{' '}
                <span className={`font-semibold ${r.dosing_in_vel_ms >= 0.3 && r.dosing_in_vel_ms <= 1.0 ? 'text-emerald-300' : 'text-amber-300'}`}>{r.dosing_in_vel_ms.toFixed(2)} m/s</span>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Outlet v</span>{' '}
                <span className={`font-semibold ${r.dosing_out_vel_ms >= 0.5 && r.dosing_out_vel_ms <= 2.0 ? 'text-emerald-300' : 'text-amber-300'}`}>{r.dosing_out_vel_ms.toFixed(2)} m/s</span>
              </div>
            </div>
          </Section>

          <Section title="4. Hydrogen dilution blowers (DSEAR / IEC 60079)" icon={<Wind className="w-4 h-4" />}
            action={<RecommendBtn onClick={() => setP(prev => ({ ...prev, ...recommendBlower(prev) }))} />}>
            <div className="grid grid-cols-2 gap-3">
              <NumField label="Dilution air ratio"     suffix=":1 (air:H2)" value={p.vent_dilution_factor}  min={10} max={200} step={1}    onChange={v => setP({ ...p, vent_dilution_factor: v })} help="Target >= 30 : 1 to stay below 25 % LEL" />
              <NumField label="Blower safety factor"   suffix="x"           value={p.blower_safety_factor}  min={1.0} max={2.0} step={0.05} onChange={v => setP({ ...p, blower_safety_factor: v })} help="Margin over computed (1.25 typical)" />
              <NumField label="Blowers (duty)"                              value={p.blower_duty}           min={1}  max={4}   step={1}    onChange={v => setP({ ...p, blower_duty: v })} />
              <NumField label="Blowers (standby)"                           value={p.blower_standby}        min={0}  max={4}   step={1}    onChange={v => setP({ ...p, blower_standby: v })} />
              <NumField label="Total static pressure"  suffix="mbar"        value={p.blower_static_mbar}    min={10} max={300} step={5}    onChange={v => setP({ ...p, blower_static_mbar: v })} help="Vent-stack + grille + line losses" />
              <NumField label="Purge time"             suffix="min"         value={p.blower_purge_min}      min={2}  max={60}  step={1}    onChange={v => setP({ ...p, blower_purge_min: v })} help="Pre-energise and post-shutdown each" />
              <SelField label="Air intake (standard)"                       value={String(p.blower_inlet_DN) as string}
                options={STD_PIPES.map(s => ({ value: String(s.dn), label: `${s.label} (ID ${s.id_mm.toFixed(1)} mm)` }))}
                onChange={v => setP({ ...p, blower_inlet_DN: parseInt(v, 10) || 80 })} />
              <SelField label="Vent discharge (standard)"                   value={String(p.blower_outlet_DN) as string}
                options={STD_PIPES.map(s => ({ value: String(s.dn), label: `${s.label} (ID ${s.id_mm.toFixed(1)} mm)` }))}
                onChange={v => setP({ ...p, blower_outlet_DN: parseInt(v, 10) || 100 })} />
            </div>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Intake v</span>{' '}
                <span className={`font-semibold ${r.blower_in_vel_ms <= 15 ? 'text-emerald-300' : 'text-amber-300'}`}>{r.blower_in_vel_ms.toFixed(1)} m/s</span>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Discharge v</span>{' '}
                <span className={`font-semibold ${r.blower_out_vel_ms <= 12 ? 'text-emerald-300' : 'text-amber-300'}`}>{r.blower_out_vel_ms.toFixed(1)} m/s</span>
              </div>
              <div className="rounded-md bg-cyan-900/20 border border-cyan-700 px-2 py-1.5">
                <span className="text-slate-400">Rec. intake</span>{' '}
                <span className="text-cyan-200 font-semibold">DN{r.recommended_blower_in_DN}</span>
              </div>
              <div className="rounded-md bg-cyan-900/20 border border-cyan-700 px-2 py-1.5">
                <span className="text-slate-400">Rec. discharge</span>{' '}
                <span className="text-cyan-200 font-semibold">DN{r.recommended_blower_out_DN}</span>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3 text-[11px]">
              <div className="rounded-md bg-cyan-900/20 border border-cyan-700 px-2 py-1.5">
                <span className="text-slate-400">Recommended flow / blower</span>{' '}
                <span className="text-cyan-200 font-semibold">{r.blowerFlow_Nm3h_each_duty.toFixed(0)} Nm3/h</span>
                <span className="text-slate-500"> = {r.blowerFlow_actual_m3h_each.toFixed(0)} m3/h @ {p.workingTemp_C.toFixed(0)} C</span>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Run time / day</span>{' '}
                <span className="text-slate-100 font-medium">{r.blower_run_hours_per_day.toFixed(1)} h</span>
                <span className="text-slate-500"> (+ {r.blower_purge_total_min} min purge)</span>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">H2 generated</span>{' '}
                <span className="text-slate-100 font-medium">{r.H2_Nm3h.toFixed(2)} Nm3/h</span>
              </div>
              <div className={`rounded-md border px-2 py-1.5 ${r.vent_safe ? 'bg-emerald-900/20 border-emerald-700' : 'bg-rose-900/20 border-rose-700'}`}>
                <span className="text-slate-400">Vent [H2]</span>{' '}
                <span className="text-slate-100 font-medium">{r.vent_H2_pc_vv.toFixed(2)} % v/v</span>
              </div>
            </div>
          </Section>

          {/* ─── H2 TANK HEADSPACE TARGET + AUTO-SIZED BLOWER ───────────── */}
          {(() => {
            const H2_gen = r.H2_Nm3h;
            const target = Math.max(0.1, Math.min(3.9, p.h2_target_pct_vv));
            const V = Math.max(0.5, p.h2_tank_volume_m3);
            // Equilibrium dilution: %v/v = H2 / (H2 + Air) × 100
            // Solve for Air: Air = H2 × (100/target − 1)
            const requiredAir_Nm3h = H2_gen * (100 / target - 1);
            const requiredAir_actual = requiredAir_Nm3h * ((273.15 + p.workingTemp_C) / 273.15) * (1013 / p.workingPressure_mbar_atm);
            const ach = requiredAir_actual / V;
            const dilutionRatio = H2_gen > 0 ? requiredAir_Nm3h / H2_gen : 0;
            const recommendedFactor = Math.ceil(dilutionRatio);
            // Clearance time from 4% (LEL) down to target — well-mixed CSTR model: τ = V/Q × ln(C0/Ct)
            const clearanceMin = V / Math.max(0.0001, requiredAir_actual) * Math.log(4 / target) * 60;
            const lelPct = (target / 4) * 100;
            const isUnsafe = target > 1.0 || lelPct > 25;
            const blowerFlowWithSF = requiredAir_Nm3h * p.blower_safety_factor;
            const perBlower = blowerFlowWithSF / Math.max(1, p.blower_duty);
            const applyToBlower = () => setP(prev => ({ ...prev, vent_dilution_factor: Math.max(10, Math.min(200, recommendedFactor)) }));
            return (
          <Section title="4a. H₂ tank headspace target → auto-sized blower" icon={<Wind className="w-4 h-4" />}
            action={
              <button onClick={applyToBlower}
                className="px-2.5 py-1 rounded-md bg-cyan-500/15 border border-cyan-500/40 text-[11px] font-bold text-cyan-300 hover:bg-cyan-500/25 transition-colors">
                ⇣ Apply to dilution ratio
              </button>
            }>
            <div className="grid grid-cols-2 gap-3">
              <NumField
                label="Max H₂ in tank headspace"
                suffix="% v/v"
                value={p.h2_target_pct_vv}
                min={0.1} max={3.9} step={0.05}
                onChange={v => setP({ ...p, h2_target_pct_vv: v })}
                help="LEL of H₂ in air = 4 % v/v. Industry standard: keep ≤ 1 % (25 % LEL) per IEC 60079 / DSEAR / NFPA 820."
              />
              <NumField
                label="Tank / enclosure headspace volume"
                suffix="m³"
                value={p.h2_tank_volume_m3}
                min={0.5} max={100} step={0.5}
                onChange={v => setP({ ...p, h2_tank_volume_m3: v })}
                help="Free vapor space above hypochlorite / brine in the storage / degassing tank that must be ventilated."
              />
            </div>

            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
              <div className="rounded-md bg-cyan-900/20 border border-cyan-700 px-2 py-1.5">
                <span className="text-slate-400">Required dilution air</span>{' '}
                <span className="text-cyan-200 font-semibold">{requiredAir_Nm3h.toFixed(1)} Nm³/h</span>
                <div className="text-[10px] text-slate-500">{requiredAir_actual.toFixed(0)} m³/h @ {p.workingTemp_C.toFixed(0)} °C</div>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Dilution ratio</span>{' '}
                <span className="text-slate-100 font-semibold">{dilutionRatio.toFixed(0)} : 1</span>
                <div className="text-[10px] text-slate-500">air : H₂ for target</div>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">ACH</span>{' '}
                <span className={`font-semibold ${ach >= 12 ? 'text-emerald-300' : ach >= 6 ? 'text-amber-300' : 'text-rose-300'}`}>{ach.toFixed(0)} /h</span>
                <div className="text-[10px] text-slate-500">≥12 recommended for H₂ spaces</div>
              </div>
              <div className={`rounded-md border px-2 py-1.5 ${isUnsafe ? 'bg-rose-900/20 border-rose-700' : 'bg-emerald-900/20 border-emerald-700'}`}>
                <span className="text-slate-400">LEL position</span>{' '}
                <span className={`font-semibold ${isUnsafe ? 'text-rose-300' : 'text-emerald-300'}`}>{lelPct.toFixed(0)} % LEL</span>
                <div className="text-[10px] text-slate-500">{isUnsafe ? '⚠ above 25 % LEL' : '✓ within DSEAR / IEC 60079'}</div>
              </div>
            </div>

            <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-2 text-[11px]">
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Clearance time</span>{' '}
                <span className="text-slate-100 font-medium">{clearanceMin.toFixed(1)} min</span>
                <div className="text-[10px] text-slate-500">4 % → target after upset</div>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Air flow × safety factor</span>{' '}
                <span className="text-slate-100 font-medium">{blowerFlowWithSF.toFixed(0)} Nm³/h</span>
                <div className="text-[10px] text-slate-500">× {p.blower_safety_factor.toFixed(2)} margin</div>
              </div>
              <div className="rounded-md bg-cyan-900/20 border border-cyan-700 px-2 py-1.5">
                <span className="text-slate-400">Per blower</span>{' '}
                <span className="text-cyan-200 font-semibold">{perBlower.toFixed(0)} Nm³/h</span>
                <div className="text-[10px] text-slate-500">{p.blower_duty} duty + {p.blower_standby} standby</div>
              </div>
            </div>

            {/* ─── Live SVG: tank + headspace H₂ + dilution blower ──────────── */}
            <div className="mt-4 rb-inner-card p-3">
              <p className="text-[10px] text-slate-500 uppercase tracking-wider font-bold mb-2">Live diagram — H₂ headspace ventilation</p>
              <svg viewBox="0 0 880 360" className="w-full h-auto">
                <defs>
                  <linearGradient id="hypoLiq" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#22d3ee" stopOpacity="0.45" />
                    <stop offset="100%" stopColor="#0e7490" stopOpacity="0.95" />
                  </linearGradient>
                  <linearGradient id="h2Gas" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#fbbf24" stopOpacity={Math.min(0.85, 0.15 + target * 0.18)} />
                    <stop offset="100%" stopColor="#fbbf24" stopOpacity={Math.min(0.95, 0.30 + target * 0.18)} />
                  </linearGradient>
                </defs>

                <text x="440" y="22" fill="#cbd5e1" fontSize="14" textAnchor="middle" fontWeight="bold" letterSpacing="2">
                  HYDROGEN VENTILATION — TANK + BLOWER · LIVE
                </text>

                {/* Tank — half cross-section */}
                <g>
                  {/* Tank outline */}
                  <rect x="80" y="60" width="260" height="240" rx="14" fill="none" stroke="#64748b" strokeWidth="2.5" />
                  {/* Hypochlorite liquid fill (bottom 55% of tank) */}
                  <rect x="82" y="190" width="256" height="108" rx="10" fill="url(#hypoLiq)" />
                  {/* Wave on liquid surface */}
                  <path d="M 82 192 Q 145 186 210 192 T 338 192 L 338 198 L 82 198 Z" fill="#22d3ee" opacity="0.55" />
                  {/* Headspace fill — colour scales with target % */}
                  <rect x="82" y="62" width="256" height="128" rx="10" fill="url(#h2Gas)" />
                  {/* Bubbles in headspace representing accumulating H₂ */}
                  {Array.from({ length: 14 }).map((_, i) => {
                    const cx = 95 + (i * 18) % 240;
                    const cy = 110 + ((i * 41) % 60);
                    const r = 2.2 + (i % 4) * 0.7;
                    return (
                      <circle key={i} cx={cx} cy={cy} r={r} fill="#fbbf24" opacity={0.5 + (i % 3) * 0.15}>
                        <animate attributeName="cy" values={`${cy};${cy - 8};${cy}`} dur={`${2.2 + (i % 5) * 0.4}s`} repeatCount="indefinite" />
                        <animate attributeName="opacity" values={`${0.4 + (i % 3) * 0.15};${0.8};${0.4 + (i % 3) * 0.15}`} dur={`${2.2 + (i % 5) * 0.4}s`} repeatCount="indefinite" />
                      </circle>
                    );
                  })}

                  {/* Tank labels */}
                  <text x="210" y="48" fill="#94a3b8" fontSize="13" textAnchor="middle" fontWeight="bold">DEGASSING TANK</text>
                  <text x="210" y="58" fill="#64748b" fontSize="11" textAnchor="middle">V = {V.toFixed(1)} m³ headspace</text>
                  {/* Liquid label */}
                  <text x="210" y="260" fill="#cbd5e1" fontSize="12" textAnchor="middle" fontWeight="bold">Hypochlorite product</text>
                  <text x="210" y="276" fill="#94a3b8" fontSize="11" textAnchor="middle">(NaOCl + brine)</text>
                  {/* H2 % label */}
                  <text x="210" y="125" fill="#0f172a" fontSize="22" textAnchor="middle" fontWeight="bold">{target.toFixed(2)} % v/v</text>
                  <text x="210" y="143" fill="#0f172a" fontSize="12" textAnchor="middle" fontWeight="bold">H₂ equilibrium target</text>
                  {/* Liquid line markers */}
                  <line x1="80" y1="190" x2="76" y2="190" stroke="#64748b" strokeWidth="2" />
                  <text x="73" y="194" fill="#94a3b8" fontSize="10" textAnchor="end">liq.</text>
                </g>

                {/* H₂ generation flow into tank */}
                <g>
                  <path d="M 80 230 L 40 230 L 40 280" fill="none" stroke="#fbbf24" strokeWidth="2.5" strokeDasharray="6 4" />
                  <polygon points="36,278 40,288 44,278" fill="#fbbf24" />
                  <text x="38" y="305" fill="#fbbf24" fontSize="12" textAnchor="end" fontWeight="bold">H₂ gen</text>
                  <text x="38" y="318" fill="#94a3b8" fontSize="11" textAnchor="end">{H2_gen.toFixed(2)} Nm³/h</text>
                </g>

                {/* Vent pipe up + over to blower */}
                <g>
                  <line x1="340" y1="80" x2="430" y2="80" stroke="#94a3b8" strokeWidth="3" />
                  <line x1="430" y1="80" x2="430" y2="160" stroke="#94a3b8" strokeWidth="3" />
                  <text x="385" y="72" fill="#94a3b8" fontSize="11" textAnchor="middle">vent to blower</text>
                </g>

                {/* Blower */}
                <g transform="translate(470, 150)">
                  <rect x="-8" y="-8" width="120" height="80" rx="8" fill="#0f172a" stroke="#06b6d4" strokeWidth="2.5" />
                  {/* Fan blades */}
                  <g transform="translate(45, 32)">
                    <circle r="22" fill="none" stroke="#06b6d4" strokeWidth="1.5" />
                    <g>
                      <line x1="0" y1="-18" x2="0" y2="18" stroke="#06b6d4" strokeWidth="3" strokeLinecap="round" />
                      <line x1="-18" y1="0" x2="18" y2="0" stroke="#06b6d4" strokeWidth="3" strokeLinecap="round" />
                      <line x1="-13" y1="-13" x2="13" y2="13" stroke="#06b6d4" strokeWidth="3" strokeLinecap="round" />
                      <line x1="-13" y1="13" x2="13" y2="-13" stroke="#06b6d4" strokeWidth="3" strokeLinecap="round" />
                      <animateTransform attributeName="transform" type="rotate" from="0" to="360" dur={`${Math.max(0.4, 2 - target).toFixed(2)}s`} repeatCount="indefinite" />
                    </g>
                    <circle r="4" fill="#06b6d4" />
                  </g>
                  <text x="52" y="89" fill="#06b6d4" fontSize="12" textAnchor="middle" fontWeight="bold">DILUTION BLOWER</text>
                  <text x="52" y="102" fill="#94a3b8" fontSize="10" textAnchor="middle">{perBlower.toFixed(0)} Nm³/h ea · {p.blower_duty}D+{p.blower_standby}S</text>
                </g>

                {/* Air intake into blower */}
                <g>
                  <line x1="600" y1="190" x2="700" y2="190" stroke="#22d3ee" strokeWidth="3" strokeDasharray="8 5" />
                  <polygon points="600,186 600,194 588,190" fill="#22d3ee" />
                  <text x="650" y="180" fill="#22d3ee" fontSize="12" textAnchor="middle" fontWeight="bold">Air intake</text>
                  <text x="650" y="208" fill="#94a3b8" fontSize="11" textAnchor="middle">{requiredAir_Nm3h.toFixed(0)} Nm³/h dilution</text>
                  {/* Intake grille */}
                  <g transform="translate(700, 175)">
                    <rect x="0" y="0" width="14" height="30" fill="#0f172a" stroke="#22d3ee" strokeWidth="1.5" />
                    {Array.from({ length: 5 }).map((_, i) => (
                      <line key={i} x1="0" y1={4 + i * 6} x2="14" y2={4 + i * 6} stroke="#22d3ee" strokeWidth="0.8" />
                    ))}
                  </g>
                </g>

                {/* Vent stack discharge */}
                <g>
                  <line x1="535" y1="142" x2="535" y2="80" stroke="#10b981" strokeWidth="3" />
                  <line x1="535" y1="80" x2="600" y2="80" stroke="#10b981" strokeWidth="3" />
                  <text x="568" y="72" fill="#10b981" fontSize="11" textAnchor="middle">diluted vent</text>
                  {/* Stack tip */}
                  <polygon points="595,75 605,80 595,85" fill="#10b981" />
                  <text x="630" y="84" fill="#10b981" fontSize="11" fontWeight="bold">to roof</text>
                </g>

                {/* LEL gauge bar */}
                <g transform="translate(770, 60)">
                  <text x="40" y="14" fill="#cbd5e1" fontSize="12" textAnchor="middle" fontWeight="bold">LEL gauge</text>
                  <text x="40" y="28" fill="#94a3b8" fontSize="10" textAnchor="middle">0 → 100 % LEL</text>
                  {/* Vertical bar */}
                  <rect x="20" y="40" width="40" height="200" rx="4" fill="#0f172a" stroke="#1e293b" />
                  {/* Green zone 0-25% LEL */}
                  <rect x="22" y="190" width="36" height="48" fill="#10b981" opacity="0.7" />
                  {/* Amber 25-50 */}
                  <rect x="22" y="140" width="36" height="50" fill="#f59e0b" opacity="0.75" />
                  {/* Red 50-100 */}
                  <rect x="22" y="42" width="36" height="98" fill="#ef4444" opacity="0.85" />
                  {/* Tick marks */}
                  <text x="65" y="244" fill="#94a3b8" fontSize="10">0</text>
                  <text x="65" y="194" fill="#94a3b8" fontSize="10">25 ← safe</text>
                  <text x="65" y="144" fill="#94a3b8" fontSize="10">50</text>
                  <text x="65" y="44"  fill="#94a3b8" fontSize="10">100</text>
                  {/* Pointer at current LEL */}
                  {(() => {
                    const lelClamped = Math.min(100, Math.max(0, lelPct));
                    const py = 240 - (lelClamped / 100) * 200;
                    return (
                      <g>
                        <line x1="15" y1={py} x2="65" y2={py} stroke="#f8fafc" strokeWidth="2" />
                        <polygon points={`12,${py - 4} 12,${py + 4} 20,${py}`} fill="#f8fafc" />
                        <text x="40" y={py - 6} fill="#f8fafc" fontSize="11" textAnchor="middle" fontWeight="bold">{lelPct.toFixed(0)}%</text>
                      </g>
                    );
                  })()}
                </g>

                {/* Bottom data strip */}
                <g transform="translate(80, 320)">
                  <rect x="0" y="0" width="720" height="32" rx="6" fill="#0f172a" stroke="#1e293b" />
                  <text x="12" y="13" fill="#64748b" fontSize="10">H₂ in</text>
                  <text x="12" y="26" fill="#fbbf24" fontSize="12" fontWeight="bold">{H2_gen.toFixed(2)} Nm³/h</text>
                  <text x="120" y="13" fill="#64748b" fontSize="10">Dilution air</text>
                  <text x="120" y="26" fill="#22d3ee" fontSize="12" fontWeight="bold">{requiredAir_Nm3h.toFixed(0)} Nm³/h</text>
                  <text x="240" y="13" fill="#64748b" fontSize="10">Ratio</text>
                  <text x="240" y="26" fill="#cbd5e1" fontSize="12" fontWeight="bold">{dilutionRatio.toFixed(0)} : 1</text>
                  <text x="320" y="13" fill="#64748b" fontSize="10">ACH</text>
                  <text x="320" y="26" fill={ach >= 12 ? '#10b981' : '#f59e0b'} fontSize="12" fontWeight="bold">{ach.toFixed(0)} /h</text>
                  <text x="380" y="13" fill="#64748b" fontSize="10">Equilibrium [H₂]</text>
                  <text x="380" y="26" fill={lelPct > 25 ? '#ef4444' : '#10b981'} fontSize="12" fontWeight="bold">{target.toFixed(2)} % v/v</text>
                  <text x="490" y="13" fill="#64748b" fontSize="10">LEL position</text>
                  <text x="490" y="26" fill={lelPct > 25 ? '#ef4444' : '#10b981'} fontSize="12" fontWeight="bold">{lelPct.toFixed(0)} %</text>
                  <text x="580" y="13" fill="#64748b" fontSize="10">Clearance τ</text>
                  <text x="580" y="26" fill="#cbd5e1" fontSize="12" fontWeight="bold">{clearanceMin.toFixed(1)} min</text>
                </g>
              </svg>
            </div>

            <p className="mt-3 text-[11px] text-slate-400 italic leading-relaxed">
              Mass balance: at well-mixed equilibrium <code className="text-slate-300">[H₂]% = Q_H₂ / (Q_H₂ + Q_air) × 100</code> → required air <code className="text-slate-300">Q_air = Q_H₂ × (100/target − 1)</code>.
              Clearance time uses the CSTR exponential model <code className="text-slate-300">τ = V/Q · ln(C₀/Cₜ)</code>.
              Hydrogen-air LEL = 4 % v/v (IEC 60079-20-1, NFPA 69). Keep headspace ≤ 1 % v/v (25 % LEL) under all upset scenarios; alarm at 10 % LEL (0.4 %), trip rectifier and ramp blower at 25 % LEL.
              The button above syncs the dilution ratio used by the main blower sizing block below. Fan speed in the diagram scales with the target — the lower the target, the faster the blower spins.
            </p>
          </Section>
            );
          })()}

          <Section title="4b. Working conditions (applied to all devices)" icon={<Thermometer className="w-4 h-4" />}>
            <div className="grid grid-cols-2 gap-3">
              <NumField label="Working temperature"   suffix="deg C" value={p.workingTemp_C}            min={-10} max={55}  step={0.5} onChange={v => setP({ ...p, workingTemp_C: v })} help="Ambient / electrical-room T - sets gas density correction for blowers and motor de-rate envelope" />
              <NumField label="Atmospheric pressure"  suffix="mbar"  value={p.workingPressure_mbar_atm} min={700} max={1100} step={1}  onChange={v => setP({ ...p, workingPressure_mbar_atm: v })} help="Site barometric pressure - 1013 mbar at sea level" />
            </div>
            <p className="mt-2 text-[11px] text-slate-400 italic">
              These conditions feed gas-volume corrections (Nm3 -&gt; m3) for blower sizing and apply uniformly to all pumps and panels for motor de-rate checks.
            </p>
          </Section>

          <Section title="5. Acid wash (electrode CIP)" icon={<FlaskConical className="w-4 h-4" />}
            action={<RecommendBtn onClick={() => setP(prev => ({ ...prev, ...recommendAcid(prev) }))} />}>
            <div className="grid grid-cols-2 gap-3">
              <SelField label="Acid type" value={p.acidType} options={[
                { value: 'HCl_15pc',      label: 'HCl 15 % (industry standard)' },
                { value: 'HCl_30pc',      label: 'HCl 30 % (heavy fouling)' },
                { value: 'sulphamic_5pc', label: 'Sulphamic acid 5 % (low corrosivity)' },
              ]} onChange={v => setP({ ...p, acidType: v })} />
              <NumField label="Wash interval"           suffix="days"   value={p.acidWashEvery_d}        min={7}  max={120} step={1}  onChange={v => setP({ ...p, acidWashEvery_d: v })} help="Tightens with hardness > 250 mg/L" />
              <NumField label="Wash duration"           suffix="min"    value={p.acidWashDuration_min}   min={15} max={240} step={5}  onChange={v => setP({ ...p, acidWashDuration_min: v })} />
              <NumField label="Wash strength (working)" suffix="% w/w"  value={p.acidWashConc_target_pc} min={1}  max={15}  step={0.5}onChange={v => setP({ ...p, acidWashConc_target_pc: v })} help="Working solution after dilution" />
              <NumField label="CIP tank volume"         suffix="m3"     value={p.acidWashTank_m3}        min={0.2} max={20} step={0.1} onChange={v => setP({ ...p, acidWashTank_m3: v })} />
              <NumField label="Acid pump head"          suffix="bar"    value={p.acidPumpHead_bar}       min={0.5} max={10} step={0.1} onChange={v => setP({ ...p, acidPumpHead_bar: v })} />
              <NumField label="Acid static pressure"    suffix="mbar"   value={p.acidPump_staticPressure_mbar} min={0} max={5000} step={10} onChange={v => setP({ ...p, acidPump_staticPressure_mbar: v })} help="CIP loop static + line losses" />
              <NumField label="Acid pumps (duty)"                       value={p.acidPumps_duty}        min={1}  max={4}  step={1}  onChange={v => setP({ ...p, acidPumps_duty: v })} />
              <NumField label="Acid pumps (standby)"                    value={p.acidPumps_standby}     min={0}  max={4}  step={1}  onChange={v => setP({ ...p, acidPumps_standby: v })} />
              <SelField label="Acid pump inlet (standard)"              value={String(p.acidPump_inlet_DN) as string}
                options={STD_PIPES.map(s => ({ value: String(s.dn), label: `${s.label} (ID ${s.id_mm.toFixed(1)} mm)` }))}
                onChange={v => setP({ ...p, acidPump_inlet_DN: parseInt(v, 10) || 25 })} />
              <SelField label="Acid pump outlet (standard)"             value={String(p.acidPump_outlet_DN) as string}
                options={STD_PIPES.map(s => ({ value: String(s.dn), label: `${s.label} (ID ${s.id_mm.toFixed(1)} mm)` }))}
                onChange={v => setP({ ...p, acidPump_outlet_DN: parseInt(v, 10) || 25 })} />
            </div>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Inlet v</span>{' '}
                <span className={`font-semibold ${r.acid_in_vel_ms >= 0.3 && r.acid_in_vel_ms <= 1.0 ? 'text-emerald-300' : 'text-amber-300'}`}>{r.acid_in_vel_ms.toFixed(2)} m/s</span>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Outlet v</span>{' '}
                <span className={`font-semibold ${r.acid_out_vel_ms >= 0.5 && r.acid_out_vel_ms <= 2.0 ? 'text-emerald-300' : 'text-amber-300'}`}>{r.acid_out_vel_ms.toFixed(2)} m/s</span>
              </div>
              <div className="rounded-md bg-cyan-900/20 border border-cyan-700 px-2 py-1.5">
                <span className="text-slate-400">Rec. inlet</span>{' '}
                <span className="text-cyan-200 font-semibold">DN{r.recommended_acid_in_DN}</span>
              </div>
              <div className="rounded-md bg-cyan-900/20 border border-cyan-700 px-2 py-1.5">
                <span className="text-slate-400">Rec. outlet</span>{' '}
                <span className="text-cyan-200 font-semibold">DN{r.recommended_acid_out_DN}</span>
              </div>
            </div>
            <p className="mt-3 text-[11px] text-slate-400 italic">
              Selected: <span className="text-cyan-300">{ACID_PROPS[p.acidType].name}</span> - {ACID_PROPS[p.acidType].useCase}.
            </p>
          </Section>

          <Section title="6. Transformer / rectifier (datasheet)" icon={<Zap className="w-4 h-4" />}>
            <div className="grid grid-cols-2 gap-3">
              <NumField label="AC primary voltage" suffix="V"   value={p.txr_AC_primary_V}  min={200} max={1200} step={5} onChange={v => setP({ ...p, txr_AC_primary_V: v })} help="Common: 400 / 415 / 480 / 690 V" />
              <NumField label="Overall efficiency" suffix="%"   value={p.txr_efficiency_pc} min={70}  max={99}   step={0.5} onChange={v => setP({ ...p, txr_efficiency_pc: v })} help="Transformer x rectifier product" />
              <SelField label="Redundancy"                       value={p.txr_redundancy} options={[
                { value: 'N',   label: 'N (no standby rectifier)' },
                { value: 'N+1', label: 'N+1 (one standby rectifier)' },
              ]} onChange={v => setP({ ...p, txr_redundancy: v })} />
            </div>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">DC power total</span>{' '}
                <span className="text-slate-100 font-semibold">{r.power_total_DC_kW.toFixed(2)} kW</span>
              </div>
              <div className="rounded-md bg-cyan-900/20 border border-cyan-700 px-2 py-1.5">
                <span className="text-slate-400">AC power total</span>{' '}
                <span className="text-cyan-200 font-semibold">{r.power_total_AC_kW.toFixed(2)} kW</span>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">AC primary current</span>{' '}
                <span className="text-slate-100 font-semibold">{(r.power_total_AC_kW * 1000 / (p.txr_AC_primary_V * Math.sqrt(3))).toFixed(0)} A (3-ph)</span>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Eff. (overall)</span>{' '}
                <span className="text-slate-100 font-semibold">{(r.txr_eff * 100).toFixed(1)} %</span>
              </div>
            </div>
          </Section>

          <Section title="7. Hypochlorite storage + H2 degassing tank" icon={<Beaker className="w-4 h-4" />}>
            <div className="grid grid-cols-2 gap-3">
              <NumField label="Tank volume"                  suffix="m3"     value={p.storage_tank_volume_m3}          min={0.5} max={50}  step={0.1} onChange={v => setP({ ...p, storage_tank_volume_m3: v })} />
              <NumField label="Degassing residence required" suffix="min"    value={p.storage_degassing_residence_min} min={5}   max={120} step={1}   onChange={v => setP({ ...p, storage_degassing_residence_min: v })} help="H2 disengagement target" />
              <SelField label="Material"                                      value={p.storage_material} options={[
                { value: 'GRP',  label: 'GRP (datasheet std.)' },
                { value: 'HDPE', label: 'HDPE' },
                { value: 'PP',   label: 'Polypropylene' },
                { value: 'PVDF', label: 'PVDF' },
              ]} onChange={v => setP({ ...p, storage_material: v })} />
              <NumField label="Freeboard"                    suffix="%"      value={p.storage_freeboard_pc}            min={5}   max={40}  step={1}   onChange={v => setP({ ...p, storage_freeboard_pc: v })} help="Gas space above design level" />
            </div>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
              <div className={`rounded-md border px-2 py-1.5 ${r.storage_degassing_ok ? 'bg-emerald-900/20 border-emerald-700' : 'bg-rose-900/20 border-rose-700'}`}>
                <span className="text-slate-400">Actual residence</span>{' '}
                <span className="text-slate-100 font-semibold">{r.storage_actual_residence_min.toFixed(1)} min</span>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Gas space</span>{' '}
                <span className="text-slate-100 font-semibold">{r.storage_gas_volume_m3.toFixed(2)} m3</span>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Material</span>{' '}
                <span className="text-slate-100 font-semibold">{p.storage_material}</span>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Working volume</span>{' '}
                <span className="text-slate-100 font-semibold">{(p.storage_tank_volume_m3 * (1 - p.storage_freeboard_pc / 100)).toFixed(2)} m3</span>
              </div>
            </div>
          </Section>

          <Section title="8. Eductor (NaOCl injection)" icon={<Activity className="w-4 h-4" />}>
            <div className="grid grid-cols-2 gap-3">
              <NumField label="Motive seawater flow"  suffix="m3/h" value={p.eductor_motive_m3h}    min={0.5} max={200} step={0.1} onChange={v => setP({ ...p, eductor_motive_m3h: v })} />
              <NumField label="Suction (NaOCl) flow"  suffix="L/h"  value={p.eductor_suction_Lh}    min={10}  max={20000} step={10} onChange={v => setP({ ...p, eductor_suction_Lh: v })} />
              <NumField label="Motive pressure"       suffix="bar"  value={p.eductor_motive_pressure_bar} min={1} max={10} step={0.1} onChange={v => setP({ ...p, eductor_motive_pressure_bar: v })} help="Drives the venturi suction" />
              <SelField label="Motive (standard)"                                  value={String(p.eductor_motive_DN) as string}
                options={STD_PIPES.map(s => ({ value: String(s.dn), label: `${s.label} (ID ${s.id_mm.toFixed(1)} mm)` }))}
                onChange={v => setP({ ...p, eductor_motive_DN: parseInt(v, 10) || 50 })} />
              <SelField label="Suction (standard)"                                 value={String(p.eductor_suction_DN) as string}
                options={STD_PIPES.map(s => ({ value: String(s.dn), label: `${s.label} (ID ${s.id_mm.toFixed(1)} mm)` }))}
                onChange={v => setP({ ...p, eductor_suction_DN: parseInt(v, 10) || 25 })} />
              <SelField label="Discharge (standard)"                               value={String(p.eductor_discharge_DN) as string}
                options={STD_PIPES.map(s => ({ value: String(s.dn), label: `${s.label} (ID ${s.id_mm.toFixed(1)} mm)` }))}
                onChange={v => setP({ ...p, eductor_discharge_DN: parseInt(v, 10) || 65 })} />
            </div>
            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Motive v</span>{' '}
                <span className={`font-semibold ${r.eductor_motive_vel >= 1 && r.eductor_motive_vel <= 4 ? 'text-emerald-300' : 'text-amber-300'}`}>{r.eductor_motive_vel.toFixed(2)} m/s</span>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Suction v</span>{' '}
                <span className={`font-semibold ${r.eductor_suction_vel >= 0.3 && r.eductor_suction_vel <= 1.5 ? 'text-emerald-300' : 'text-amber-300'}`}>{r.eductor_suction_vel.toFixed(2)} m/s</span>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Discharge v</span>{' '}
                <span className={`font-semibold ${r.eductor_discharge_vel >= 1 && r.eductor_discharge_vel <= 3 ? 'text-emerald-300' : 'text-amber-300'}`}>{r.eductor_discharge_vel.toFixed(2)} m/s</span>
              </div>
              <div className={`rounded-md border px-2 py-1.5 ${r.eductor_ratio_ok ? 'bg-emerald-900/20 border-emerald-700' : 'bg-amber-900/20 border-amber-700'}`}>
                <span className="text-slate-400">Motive / suction</span>{' '}
                <span className="text-slate-100 font-semibold">{r.eductor_ratio.toFixed(1)} : 1</span>
              </div>
              <div className="rounded-md bg-slate-900/40 border border-slate-700 px-2 py-1.5">
                <span className="text-slate-400">Discharge flow</span>{' '}
                <span className="text-slate-100 font-semibold">{r.eductor_discharge_m3h.toFixed(2)} m3/h</span>
              </div>
            </div>
            <p className="mt-2 text-[11px] text-slate-400 italic">
              Eductor draws hypochlorite into the motive seawater stream by venturi action. Envelope: motive 1 - 4 m/s, suction 0.3 - 1.5 m/s,
              discharge 1 - 3 m/s, motive/suction ratio 3 : 1 - 10 : 1. Wetted materials: PVDF / GRP / Hastelloy C-276.
            </p>
          </Section>

          <button
            onClick={() => setP(DEFAULTS)}
            className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-600 text-slate-200 text-sm py-2"
          >
            <RotateCcw className="w-4 h-4" /> Reset to datasheet defaults
          </button>
        </div>

        {/* OUTPUTS COLUMN */}
        <div className="space-y-5">
          <Section title="A. Sizing summary - cells, current, power" icon={<Settings className="w-4 h-4" />}>
            <div className="grid grid-cols-2 gap-3">
              <KPI label="Modules running"            value={p.modules_installed}                            unit="(+ standby)" />
              <KPI label="Stack voltage"              value={r.stackVoltage_V.toFixed(0)}                    unit="V" />
              <KPI label="Current / module"           value={r.I_per_module_A.toFixed(0)}                    unit="A" />
              <KPI label="Total active area"          value={r.cellArea_total_m2.toFixed(2)}                 unit="m^2" />
              <KPI label="DC power total"             value={r.power_total_DC_kW.toFixed(1)}                 unit="kW" />
              <KPI label="AC power total"             value={r.power_total_AC_kW.toFixed(1)}                 unit="kW"
                tone={r.specificEnergy_kWh_per_kgCl2 < 5.5 ? 'good' : 'warn'} />
              <KPI label="Spec. energy"               value={r.specificEnergy_kWh_per_kgCl2.toFixed(2)}      unit="kWh / kg Cl2" />
              <KPI label="NaOCl product"              value={r.naocl_pc_w.toFixed(2)}                        unit="% w/w" />
            </div>
          </Section>

          <Section title="B. Hypochlorite dosing pumps - flow rates & power" icon={<Activity className="w-4 h-4" />}>
            <div className="grid grid-cols-2 gap-3">
              <KPI label="Demand (Cl2 -> NaOCl)"       value={r.dosingPumpFlow_total_demand_Lh.toFixed(1)}  unit="L/h" tone="neutral" />
              <KPI label="Installed total"             value={r.dosingPumpFlow_total_Lh.toFixed(1)}         unit="L/h" />
              <KPI label="Per duty pump"               value={r.dosingPumpFlow_per_duty_Lh.toFixed(1)}      unit="L/h each" />
              <KPI label="Pump motor (each)"           value={r.dosingPump_kW_each.toFixed(3)}              unit="kW" tone="good" />
              <KPI label="Total dosing power"          value={(r.dosingPump_kW_each * p.dosingPumps_duty).toFixed(3)} unit="kW" tone="good" />
              <KPI label="Storage tank"                value={r.storageTank_m3.toFixed(2)}                  unit="m^3" />
              <KPI label="Pumps installed"             value={`${p.dosingPumps_duty} + ${p.dosingPumps_standby}`} unit="duty + standby" />
              <KPI label="Discharge head + static"     value={`${p.dosingPumpHead_bar.toFixed(1)} bar + ${p.dosingPump_staticPressure_mbar} mbar`} unit="" />
            </div>
            <p className="mt-2 text-[11px] text-slate-400">
              Sized per duty pump with N+1 redundancy. Diaphragm metering pumps (Grundfos DDA / ProMinent gamma / Watson-Marlow Qdos)
              are typical; PVDF or PTFE wetted parts for NaOCl service.
            </p>
          </Section>

          <Section title="C. Acid wash chemistry & dosing pumps" icon={<Beaker className="w-4 h-4" />}>
            <div className="grid grid-cols-2 gap-3">
              <KPI label="Wash volume / event"        value={r.wash_volume_per_event_m3.toFixed(2)}          unit="m^3" />
              <KPI label="Active acid / event"        value={r.active_acid_kg_per_event.toFixed(1)}          unit="kg" />
              <KPI label="Concentrate / event"        value={(r.concentrate_m3_per_event * 1000).toFixed(0)} unit="L" />
              <KPI label="Events / year"              value={r.events_per_year.toFixed(1)}                   unit="/ yr" />
              <KPI label="Concentrate / year"         value={r.concentrate_m3_per_year.toFixed(2)}           unit="m^3" />
              <KPI label="Acid mass / year"           value={r.concentrate_kg_per_year.toFixed(0)}           unit="kg" />
              <KPI label="Acid pump flow"             value={r.acidPump_per_duty_Lh.toFixed(0)}              unit="L/h per duty" />
              <KPI label="Acid pump motor (each)"     value={r.acidPump_kW_each.toFixed(3)}                  unit="kW" tone="good" />
              <KPI label="Acid pump total power"      value={(r.acidPump_kW_each * p.acidPumps_duty).toFixed(3)} unit="kW" tone="good" />
            </div>
            <p className="mt-2 text-[11px] text-slate-400">
              CIP loop: drain seawater, fill from CIP tank, circulate {p.acidWashDuration_min} min, drain to neutralisation tank,
              rinse with permeate. Materials of construction: GRP / PVDF / PTFE / Hastelloy C-276 for wetted parts.
            </p>
          </Section>

          <Section title="D. Hydrogen safety + blower sizing" icon={<Flame className="w-4 h-4" />}>
            <div className="grid grid-cols-2 gap-3">
              <KPI label="H2 produced"                value={r.H2_Nm3h.toFixed(2)}                           unit="Nm3/h" />
              <KPI label="Dilution air required"      value={r.dilutionAir_Nm3h.toFixed(0)}                  unit="Nm3/h" />
              <KPI label="Vent [H2]"                  value={r.vent_H2_pc_vv.toFixed(2)}                     unit="% v/v" tone={r.vent_safe ? 'good' : 'bad'} />
              <KPI label="LEL margin"                 value={(100 - (r.vent_H2_pc_vv / 4) * 100).toFixed(0)} unit="% below LEL" tone={r.vent_safe ? 'good' : 'bad'} />
              <KPI label="Recommended blower flow"    value={r.blowerFlow_Nm3h_each_duty.toFixed(0)}         unit="Nm3/h per duty" tone="good" />
              <KPI label="Actual flow @ working T"    value={r.blowerFlow_actual_m3h_each.toFixed(0)}        unit="m3/h per duty" />
              <KPI label="Blower motor (each)"        value={r.blower_motor_kW_each.toFixed(3)}              unit="kW" tone="good" />
              <KPI label="Blower total power"         value={(r.blower_motor_kW_each * p.blower_duty).toFixed(3)} unit="kW" tone="good" />
              <KPI label="Total static pressure"      value={p.blower_static_mbar.toFixed(0)}                unit="mbar" />
              <KPI label="Blowers installed"          value={`${p.blower_duty} + ${p.blower_standby}`}       unit="duty + standby" />
              <KPI label="Run time / day"             value={r.blower_run_hours_per_day.toFixed(1)}          unit="h/d" />
              <KPI label="Purge before / after"       value={p.blower_purge_min}                             unit="min each" />
              <KPI label="Density correction"         value={r.densityCorr.toFixed(3)}                       unit="Nm3 -> m3" />
            </div>
            <p className="mt-2 text-[11px] text-amber-300">
              H2 LEL = 4 % v/v. Vent stack design must keep concentration &lt;= 25 % LEL (1 % v/v) per IEC 60079 / DSEAR.
              Zone-2 classification within 1.5 m radius of vent termination. Blower interlock with rectifier:
              blower starts {p.blower_purge_min} min BEFORE rectifier energise and runs {p.blower_purge_min} min AFTER de-energise.
            </p>
          </Section>

          <Section title={`E. Engineering validation (${passCount} / ${totalChecks} pass)`} icon={<ShieldCheck className="w-4 h-4" />}>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-slate-400 border-b border-slate-700">
                  <tr>
                    <th className="text-left py-1.5">Criterion</th>
                    <th className="text-right py-1.5">Value</th>
                    <th className="text-right py-1.5">Limit</th>
                    <th className="text-center py-1.5">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {checks.map((c, i) => (
                    <tr key={i} className="border-b border-slate-800/50">
                      <td className="py-1 text-slate-200">{c.label}{c.critical && <span className="ml-1 text-rose-400">*</span>}</td>
                      <td className="py-1 text-right text-slate-100 font-medium">{c.value}</td>
                      <td className="py-1 text-right text-slate-400">{c.limit}</td>
                      <td className="py-1 text-center">
                        {c.pass
                          ? <CheckCircle2 className="w-4 h-4 text-emerald-400 inline" />
                          : <AlertCircle  className="w-4 h-4 text-rose-400 inline" />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="mt-2 text-[10px] text-slate-500">* critical limit (failure indicates redesign required)</p>
          </Section>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Section title="Sensitivity - salinity vs specific energy" icon={<Thermometer className="w-4 h-4" />}>
          <div className="h-56">
            <ResponsiveContainer>
              <LineChart data={sweepSal}>
                <CartesianGrid stroke="#334155" strokeDasharray="3 3" />
                <XAxis dataKey="salinity" stroke="#94a3b8" tick={{ fontSize: 11 }} label={{ value: 'Salinity (ppt)', position: 'insideBottom', offset: -2, fill: '#94a3b8', fontSize: 11 }} />
                <YAxis stroke="#94a3b8" tick={{ fontSize: 11 }} label={{ value: 'kWh / kg Cl2', angle: -90, position: 'insideLeft', fill: '#94a3b8', fontSize: 11 }} />
                <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155' }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <ReferenceLine x={p.salinity_ppt} stroke="#f59e0b" strokeDasharray="3 3" label={{ value: 'design', fill: '#f59e0b', fontSize: 10 }} />
                <Line type="monotone" dataKey="specEnergy" stroke="#22d3ee" strokeWidth={2} dot={false} name="Specific energy" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Section>

        <Section title="Sensitivity - temperature vs specific energy" icon={<Thermometer className="w-4 h-4" />}>
          <div className="h-56">
            <ResponsiveContainer>
              <LineChart data={sweepT}>
                <CartesianGrid stroke="#334155" strokeDasharray="3 3" />
                <XAxis dataKey="temp" stroke="#94a3b8" tick={{ fontSize: 11 }} label={{ value: 'Temperature (deg C)', position: 'insideBottom', offset: -2, fill: '#94a3b8', fontSize: 11 }} />
                <YAxis stroke="#94a3b8" tick={{ fontSize: 11 }} label={{ value: 'kWh / kg Cl2', angle: -90, position: 'insideLeft', fill: '#94a3b8', fontSize: 11 }} />
                <Tooltip contentStyle={{ background: '#0f172a', border: '1px solid #334155' }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <ReferenceLine x={p.temperature_C} stroke="#f59e0b" strokeDasharray="3 3" label={{ value: 'design', fill: '#f59e0b', fontSize: 10 }} />
                <Line type="monotone" dataKey="specEnergy" stroke="#a78bfa" strokeWidth={2} dot={false} name="Specific energy" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Section>
      </div>

      <Section title="Engineering basis & references" icon={<Info className="w-4 h-4" />}>
        <div className="space-y-2 text-[12px] text-slate-300 leading-relaxed">
          <p>
            <span className="text-cyan-300 font-semibold">Faraday yield:</span> 1.3245 g Cl2 / Ah at 100 % current efficiency
            (M_Cl2 = 71 g/mol; n = 2 electrons; F = 96 485 C/mol). Practical yield = theoretical x current efficiency
            (typically 0.85 - 0.95 for single-pass seawater on MMO Pt/Ir anodes).
          </p>
          <p>
            <span className="text-cyan-300 font-semibold">Standard seawater:</span> 35 ppt salinity (open ocean), ~ 19 g/L Cl-,
            density ~ 1025 kg/m3 at 20 deg C, conductivity ~ 43 mS/cm at 15 deg C, pH 8.0 - 8.2, alkalinity ~ 120 mg/L as CaCO3.
            Inputs may be reduced for brackish or recirculated brine streams.
          </p>
          <p>
            <span className="text-cyan-300 font-semibold">Cell envelope:</span> 2 - 4 kA/m2 current density, 3.5 - 4.5 V per cell,
            0.4 - 0.8 m/s brine velocity. Product hypochlorite typically 0.4 - 0.8 % w/w available Cl2 from single-pass seawater electrolysis.
          </p>
          <p>
            <span className="text-cyan-300 font-semibold">Hydrogen safety:</span> 1:1 molar H2 : Cl2 yield. Vent design (IEC 60079 / DSEAR)
            requires dilution to &lt;= 25 % of LEL = 1 % v/v in air (LEL_H2 = 4 % v/v). Zone-2 hazardous area within 1.5 m of vent termination.
          </p>
          <p>
            <span className="text-cyan-300 font-semibold">Acid wash:</span> HCl 15 % is the industry-standard CIP for cathodic
            CaCO3 / Mg(OH)2 scale (target wash strength 3 - 10 % w/w; interval 14 - 60 d depending on hardness). Sulphamic acid 5 %
            is a lower-corrosivity alternative for stainless-component compatibility.
          </p>
          <p className="text-slate-500 italic text-[11px]">
            References: Bommaraju &amp; Orosco, "Brine Electrolysis" (ECS); Bennett (1980) "Electrodes for the generation of Cl2 / O2 from seawater";
            De Nora technical bulletins; IEC 60079; DSEAR; HSE INDG370.
          </p>
        </div>
      </Section>
      </>
      )}

      <div className="text-[10px] text-slate-500 text-center pt-2 border-t border-slate-800">
        Reynolds &amp; Bauhm Smart Water - Reservoir Suite V 2.0 - Electrochlorination Sizing Tool
      </div>
    </div>
  );
}
